/**
 * Custom JavaScript for non-critical behavior.
 * This file is loaded with defer for custom behavior that can run after parsing.
 */

window.CustomShopify = window.CustomShopify || {};

window.CustomShopify.DiamondVideoCard = (function () {
  var VIEWER_SOURCE_MAX_SIZE = 720;
  var VIEWER_SOURCE_MIN_SIZE_DESKTOP = 480;
  var VIEWER_SOURCE_MIN_SIZE_MOBILE = 560;
  var VIEWER_SOURCE_OVERSCAN_RATIO_DESKTOP = 1.12;
  var VIEWER_SOURCE_OVERSCAN_RATIO_MOBILE = 1.12;
  var DESKTOP_MAX_SIZE = 900;
  var DESKTOP_MIN_SIZE = 600;
  var DESKTOP_MIN_VIEWPORT_SIZE = 420;
  var DESKTOP_OUTER_GUTTER = 64;
  var DESKTOP_FRAME_CHROME_HEIGHT = 176;
  var MOBILE_MAX_SIZE = 640;
  var MOBILE_MIN_SIZE = 240;
  var MOBILE_HORIZONTAL_GUTTER = 40;
  var MOBILE_VERTICAL_CHROME = 136;
  /** @type {Record<string, { openedAt: number, titleProduct: string }>} */
  var openStates = {};

  /**
   * @param {number} displaySize
   * @param {number} minSourceSize
   * @param {number} overscanRatio
   * @returns {number}
   */
  function getSourceSize(displaySize, minSourceSize, overscanRatio) {
    return Math.max(
      minSourceSize,
      Math.min(
        Math.round(displaySize * overscanRatio),
        VIEWER_SOURCE_MAX_SIZE,
      ),
    );
  }

  /**
   * @param {string} url
   * @returns {{ url: string, width: number, height: number, sourceWidth: number, sourceHeight: number, scale: number }}
   */
  function getViewerState(url) {
    var dimensions = extractDimensions(url);

    if (!url) {
      return {
        url: url,
        width: dimensions.width,
        height: dimensions.height,
        sourceWidth: dimensions.width,
        sourceHeight: dimensions.height,
        scale: 1,
      };
    }

    if (!window.matchMedia("(max-width: 749px)").matches) {
      var desktopAvailableWidth = Math.max(
        DESKTOP_MIN_VIEWPORT_SIZE,
        Math.min(window.innerWidth - DESKTOP_OUTER_GUTTER, DESKTOP_MAX_SIZE),
      );
      var desktopAvailableHeight = Math.max(
        DESKTOP_MIN_VIEWPORT_SIZE,
        Math.min(
          Math.floor(window.innerHeight * 0.9) - DESKTOP_FRAME_CHROME_HEIGHT,
          DESKTOP_MAX_SIZE,
        ),
      );
      var desktopSize = Math.min(
        Math.max(dimensions.width, DESKTOP_MIN_SIZE),
        desktopAvailableWidth,
        desktopAvailableHeight,
      );
      var desktopSourceSize = getSourceSize(
        desktopSize,
        VIEWER_SOURCE_MIN_SIZE_DESKTOP,
        VIEWER_SOURCE_OVERSCAN_RATIO_DESKTOP,
      );

      return {
        url: replaceDimensions(url, desktopSourceSize, desktopSourceSize),
        width: desktopSize,
        height: desktopSize,
        sourceWidth: desktopSourceSize,
        sourceHeight: desktopSourceSize,
        scale: desktopSize / desktopSourceSize,
      };
    }

    var viewportWidth = Math.max(
      1,
      Math.min(window.innerWidth - MOBILE_HORIZONTAL_GUTTER, MOBILE_MAX_SIZE),
    );
    var viewportHeight = Math.max(
      1,
      Math.min(window.innerHeight - MOBILE_VERTICAL_CHROME, MOBILE_MAX_SIZE),
    );
    var size = Math.round(
      Math.min(
        Math.max(MOBILE_MIN_SIZE, Math.min(viewportWidth, viewportHeight)),
        viewportWidth,
        viewportHeight,
      ),
    );
    var mobileSourceSize = getSourceSize(
      size,
      VIEWER_SOURCE_MIN_SIZE_MOBILE,
      VIEWER_SOURCE_OVERSCAN_RATIO_MOBILE,
    );

    return {
      url: replaceDimensions(url, mobileSourceSize, mobileSourceSize),
      width: size,
      height: size,
      sourceWidth: mobileSourceSize,
      sourceHeight: mobileSourceSize,
      scale: size / mobileSourceSize,
    };
  }

  /**
   * @param {string} url
   * @param {number} width
   * @param {number} height
   * @returns {string}
   */
  function replaceDimensions(url, width, height) {
    var resizedUrl = url.replace(
      /\/video\/\d+\/\d+(?=\/autoplay|\?|$)/,
      "/video/" + width + "/" + height,
    );

    if (resizedUrl === url && !/\/video\/\d+\/\d+(?:\/autoplay)?(?:\?|$)/.test(url)) {
      return url;
    }

    if (resizedUrl.indexOf("/autoplay") === -1) {
      resizedUrl = resizedUrl.replace(
        /\/video\/\d+\/\d+(?=\?|$)/,
        "/video/" + width + "/" + height + "/autoplay",
      );
    }

    return resizedUrl;
  }

  /**
   * @param {string} url
   * @returns {{ width: number, height: number }}
   */
  function extractDimensions(url) {
    var matches = url && url.match(/\/video\/(\d+)\/(\d+)(?=\/autoplay|\?|$)/);
    var width = matches && matches[1] ? Number(matches[1]) : 500;
    var height = matches && matches[2] ? Number(matches[2]) : width;

    if (!Number.isFinite(width) || width <= 0) {
      width = 500;
    }

    if (!Number.isFinite(height) || height <= 0) {
      height = width;
    }

    return {
      width: width,
      height: height,
    };
  }

  /**
   * @param {string} dialogId
   * @returns {HTMLDialogElement | null}
   */
  function getDialog(dialogId) {
    var dialog = document.getElementById(dialogId);

    if (!(dialog instanceof HTMLDialogElement)) {
      return null;
    }

    return dialog;
  }

  /**
   * @param {string} iframeId
   * @returns {HTMLIFrameElement | null}
   */
  function getIframe(iframeId) {
    var iframe = document.getElementById(iframeId);

    if (!(iframe instanceof HTMLIFrameElement)) {
      return null;
    }

    return iframe;
  }

  /**
   * @param {Record<string, unknown>} eventObject
   * @returns {void}
   */
  function pushAnalyticsEvent(eventObject) {
    var analytics = window.CustomShopify && window.CustomShopify.Analytics;

    if (!analytics || typeof analytics.pushEvent !== "function") {
      return;
    }

    analytics.pushEvent(eventObject).catch(function () {});
  }

  /**
   * @param {unknown} tracker
   * @returns {"list" | "pdp"}
   */
  function normalizeTracker(tracker) {
    return tracker === "pdp" ? "pdp" : "list";
  }

  /**
   * @param {unknown} productTitle
   * @returns {string}
   */
  function normalizeProductTitle(productTitle) {
    return typeof productTitle === "string" ? productTitle : "";
  }

  /**
   * @param {string} dialogId
   * @param {string} iframeId
   * @param {string} desktopUrl
   * @param {string | null} tracker
   * @param {string | null} productTitle
   * @returns {void}
   */
  function open(dialogId, iframeId, desktopUrl, tracker, productTitle) {
    var dialog = getDialog(dialogId);
    var iframe = getIframe(iframeId);
    var viewerState = getViewerState(desktopUrl);
    var titleProduct = normalizeProductTitle(productTitle);

    if (!dialog || !iframe || !viewerState.url) {
      return;
    }

    dialog.style.setProperty("--twc-diamond-video-width", String(viewerState.width) + "px");
    dialog.style.setProperty("--twc-diamond-video-height", String(viewerState.height) + "px");
    dialog.style.setProperty(
      "--twc-diamond-video-source-width",
      String(viewerState.sourceWidth) + "px",
    );
    dialog.style.setProperty(
      "--twc-diamond-video-source-height",
      String(viewerState.sourceHeight) + "px",
    );
    dialog.style.setProperty("--twc-diamond-video-scale", String(viewerState.scale));
    iframe.src = viewerState.url;
    openStates[dialogId] = {
      openedAt: Date.now(),
      titleProduct: titleProduct,
    };
    pushAnalyticsEvent({
      event: "product:360",
      tracker: normalizeTracker(tracker),
      title_product: titleProduct,
    });
  }

  /**
   * @param {string} dialogId
   * @param {string} iframeId
   * @returns {void}
   */
  function close(dialogId, iframeId) {
    var dialog = getDialog(dialogId);
    var iframe = getIframe(iframeId);

    if (dialog) {
      dialog.style.removeProperty("--twc-diamond-video-width");
      dialog.style.removeProperty("--twc-diamond-video-height");
      dialog.style.removeProperty("--twc-diamond-video-source-width");
      dialog.style.removeProperty("--twc-diamond-video-source-height");
      dialog.style.removeProperty("--twc-diamond-video-scale");
    }

    var openState = openStates[dialogId];

    if (openState) {
      delete openStates[dialogId];
      pushAnalyticsEvent({
        event: "product:close-360",
        title_product: openState.titleProduct,
        duration: Math.round(((Date.now() - openState.openedAt) / 1000) * 100) / 100,
      });
    }

    if (!iframe) {
      return;
    }

    iframe.removeAttribute("src");
  }

  return {
    open: open,
    close: close,
  };
})();

window.CustomShopify.ProductComparison = (function () {
  var STORAGE_KEY = "custom_product_comparison_v1";
  var MIN_COMPARE_ITEMS = 2;
  var FALLBACK_MAX_ITEMS = 4;
  /** @type {any} */
  var launcherShellElement = null;
  /** @type {any} */
  var launcherElement = null;
  /** @type {any} */
  var modalElement = null;
  /** @type {any} */
  var tableHostElement = null;
  /** @type {any} */
  var bodyElement = null;
  var controlsRefreshScheduled = false;
  /** @type {any} */
  var activeDesktopScrollCleanup = null;
  var activeCompareSource = "manual";
  /** @type {Array<{ id: string, url: string, currentPage?: boolean }>} */
  var activeTransientSelection = [];
  /** @type {Map<string, { type: string, title: string, image: string }>} */
  var selectionMetaCache = new Map();

  /**
   * @returns {number}
   */
  function getMaxItems() {
    var config = window.CustomShopify && window.CustomShopify.ProductComparisonConfig;
    var configured = config && typeof config.maxItems === "number" ? Math.round(config.maxItems) : FALLBACK_MAX_ITEMS;
    return Math.max(2, Math.min(7, configured));
  }

  /**
   * @returns {Record<string, string>}
   */
  function getMessages() {
    var config = window.CustomShopify && window.CustomShopify.ProductComparisonConfig;
    var defaults = {
      launcherLabel: "Confronta",
      modalEyebrow: "Comparatore Rosellini",
      modalTitle: "Confronta i tuoi gioielli",
      modalSubtitle:
        "I valori evidenziati in verde sono i migliori rispetto alla metrica impostata nel JSON prodotto.",
      close: "Chiudi confronto",
      clear: "Svuota selezione",
      remove: "Rimuovi",
      loading: "Caricamento comparazione in corso...",
      empty: "Non ci sono abbastanza prodotti con JSON di comparazione disponibile. Seleziona altri prodotti.",
      needTwoProducts: "Seleziona almeno 2 prodotti per avviare la comparazione.",
      alreadyAdded: "Questo prodotto e gia presente nella comparazione.",
      limitReached: "Hai raggiunto il limite di prodotti. Rimuovine qualcuno prima di aggiungerne altri.",
      typeMismatchTemplate:
        "Puoi confrontare solo prodotti dello stesso tipo. Rimuovi i prodotti attuali o scegli un prodotto di tipo __TYPE__.",
      refreshFailed:
        "Non riesco a verificare i prodotti gia selezionati. Riprova tra qualche secondo prima di aggiungerne altri.",
      priceFrom: "Prezzo",
      higherBetter: "Piu alto e meglio",
      lowerBetter: "Piu basso e meglio",
      tableAriaLabel: "Tabella comparazione prodotti",
      actionsLabel: "Azioni",
      viewProduct: "Vedi prodotto",
      addToCart: "Aggiungi al carrello",
      buyNow: "Acquista ora",
      unavailable: "Non disponibile",
      chooseVariant: "Seleziona una variante",
      addingToCart: "Aggiunta in corso...",
      addToCartError: "Non sono riuscito ad aggiungere la variante al carrello. Riprova.",
    };
    var configured = config && config.messages && typeof config.messages === "object" ? config.messages : {};
    return Object.assign({}, defaults, configured);
  }

  /**
   * @param {string} maybeUrl
   * @returns {string}
   */
  function normalizeUrl(maybeUrl) {
    if (typeof maybeUrl !== "string" || !maybeUrl) {
      return "";
    }

    try {
      var normalized = new URL(maybeUrl, window.location.origin);
      return normalized.pathname + normalized.search;
    } catch (_error) {
      return "";
    }
  }

  /**
   * @param {string} maybeUrl
   * @returns {string}
   */
  function normalizePathname(maybeUrl) {
    if (typeof maybeUrl !== "string" || !maybeUrl) {
      return "";
    }

    try {
      return new URL(maybeUrl, window.location.origin).pathname;
    } catch (_error) {
      return "";
    }
  }

  /**
   * @param {string} maybeUrl
   * @returns {string}
   */
  function normalizeProductPath(maybeUrl) {
    return normalizePathname(maybeUrl).replace(/\/$/, "");
  }

  /**
   * @param {string} requestedUrl
   * @param {string} finalUrl
   * @returns {boolean}
   */
  function hasProductComparisonRedirect(requestedUrl, finalUrl) {
    var requestedPath = normalizeProductPath(requestedUrl);
    var finalPath = normalizeProductPath(finalUrl);
    return Boolean(requestedPath && finalPath && requestedPath !== finalPath);
  }

  /**
   * @param {string} maybeUrl
   * @returns {string}
   */
  function buildProductJsonUrl(maybeUrl) {
    var pathname = normalizePathname(maybeUrl);
    if (!pathname) {
      return "";
    }

    var normalizedPath = pathname.replace(/\/$/, "");
    if (/\.js$/i.test(normalizedPath)) {
      return normalizedPath;
    }

    return normalizedPath + ".js";
  }

  /**
   * @param {string} maybeUrl
   * @returns {string}
   */
  function getVariantIdFromUrl(maybeUrl) {
    if (typeof maybeUrl !== "string" || !maybeUrl) {
      return "";
    }

    try {
      return new URL(maybeUrl, window.location.origin).searchParams.get("variant") || "";
    } catch (_error) {
      return "";
    }
  }

  /**
   * @param {string} variantId
   * @returns {string}
   */
  function buildBuyNowUrl(variantId) {
    if (!variantId) {
      return "";
    }

    var cartPath = Theme && Theme.routes && typeof Theme.routes.cart_url === "string" ? Theme.routes.cart_url : "/cart";
    return cartPath + "/" + encodeURIComponent(variantId) + ":1?checkout";
  }

  /**
   * @param {unknown} value
   * @returns {string}
   */
  function escapeHtml(value) {
    return String(value === null || typeof value === "undefined" ? "" : value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  /**
   * @param {unknown} value
   * @returns {string}
   */
  function escapeAttribute(value) {
    return escapeHtml(value);
  }

  /**
   * @param {string} url
   * @returns {Promise<null | { title?: string, options?: unknown[], variants?: unknown[] }>}
   */
  async function fetchProductJson(url) {
    var productJsonUrl = buildProductJsonUrl(url);
    if (!productJsonUrl) {
      return null;
    }

    try {
      var response = await window.fetch(productJsonUrl, { credentials: "same-origin" });
      if (!response.ok) {
        return null;
      }

      return await response.json();
    } catch (_error) {
      return null;
    }
  }

  /**
   * @param {null | { title?: string, options?: unknown[], variants?: unknown[] }} productJson
   * @param {string} selectedUrl
   * @returns {{ title: string, options: string[], variants: Array<{ id: string, title: string, available: boolean, selected: boolean }> } | null}
   */
  function normalizeProductData(productJson, selectedUrl) {
    if (!productJson || !Array.isArray(productJson.variants)) {
      return null;
    }

    var optionNames = Array.isArray(productJson.options)
      ? productJson.options.filter(function (optionName) {
          return typeof optionName === "string" && optionName.trim();
        }).map(function (optionName) {
          return optionName.trim();
        })
      : [];
    var preferredVariantId = getVariantIdFromUrl(selectedUrl);
    var firstAvailableVariantId = "";
    var normalizedVariants = productJson.variants
      .filter(function (variant) {
        return Boolean(variant) && typeof variant === "object" && ("id" in variant);
      })
      .map(function (variant) {
        var variantObject = /** @type {{ id?: string | number, title?: string, available?: boolean }} */ (variant);
        var variantId = typeof variantObject.id === "number" || typeof variantObject.id === "string"
          ? String(variantObject.id)
          : "";
        if (!firstAvailableVariantId && variantObject.available) {
          firstAvailableVariantId = variantId;
        }

        return {
          id: variantId,
          title:
            typeof variantObject.title === "string" && variantObject.title.trim()
              ? variantObject.title.trim()
              : "Default Title",
          available: Boolean(variantObject.available),
          selected: false,
        };
      })
      .filter(function (variant) {
        return Boolean(variant.id);
      });

    if (!normalizedVariants.length) {
      return null;
    }

    var selectedVariantId = preferredVariantId || firstAvailableVariantId || normalizedVariants[0].id;
    normalizedVariants.forEach(function (variant) {
      variant.selected = variant.id === selectedVariantId;
    });

    return {
      title: typeof productJson.title === "string" ? productJson.title : "",
      options: optionNames,
      variants: normalizedVariants,
    };
  }

  /**
   * @param {Element} node
   * @returns {{ id: string, url: string, type: string, title: string, image: string }}
   */
  function readProductFromNode(node) {
    var dataset = node.dataset || {};
    var idValue = typeof dataset.productId === "string" ? dataset.productId : "";
    var normalizedUrl = normalizeUrl(dataset.productUrl || "");

    return {
      id: idValue,
      url: normalizedUrl,
      type: (dataset.productType || "generic").trim() || "generic",
      title: (dataset.productTitle || "").trim(),
      image: (dataset.productImage || "").trim(),
    };
  }

  /**
   * @param {unknown} value
   * @returns {value is { items: Array<{ id: string, url: string }> }}
   */
  function isValidStorageShape(value) {
    if (!value || typeof value !== "object") {
      return false;
    }

    var maybeItems = value.items;
    if (!Array.isArray(maybeItems)) {
      return false;
    }

    return true;
  }

  /**
   * @returns {Array<{ id: string, url: string }>}
   */
  function loadSelection() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) {
        return [];
      }

      var parsed = JSON.parse(raw);
      if (!isValidStorageShape(parsed)) {
        return [];
      }

      /** @type {Set<string>} */
      var byId = new Set();
      /** @type {Set<string>} */
      var byUrl = new Set();
      /** @type {Array<{ id: string, url: string }>} */
      var cleaned = [];

      parsed.items.forEach(function (item) {
        if (!item || typeof item !== "object") {
          return;
        }

        var normalizedId = typeof item.id === "string" ? item.id : "";
        var normalizedUrl = normalizeUrl(typeof item.url === "string" ? item.url : "");
        if (!normalizedUrl) {
          return;
        }

        if ((normalizedId && byId.has(normalizedId)) || (normalizedUrl && byUrl.has(normalizedUrl))) {
          return;
        }

        if (normalizedId) {
          byId.add(normalizedId);
        }

        if (normalizedUrl) {
          byUrl.add(normalizedUrl);
        }

        cleaned.push({
          id: normalizedId,
          url: normalizedUrl,
        });
      });

      return cleaned;
    } catch (_error) {
      return [];
    }
  }

  /**
   * @param {Array<{ id: string, url: string }>} selection
   * @returns {void}
   */
  function saveSelection(selection) {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        items: selection,
      }),
    );
  }

  /**
   * @param {{ id: string, url: string }} item
   * @returns {string}
   */
  function getSelectionCacheKey(item) {
    if (item.url) {
      return "url:" + item.url;
    }
    if (item.id) {
      return "id:" + item.id;
    }
    return "";
  }

  /**
   * @param {{ id: string, url: string }} item
   * @returns {{ type: string, title: string, image: string } | null}
   */
  function getCachedMeta(item) {
    var key = getSelectionCacheKey(item);
    if (!key) {
      return null;
    }
    return selectionMetaCache.get(key) || null;
  }

  /**
   * @param {{ id: string, url: string }} item
   * @param {{ type: string, title: string, image: string }} meta
   * @returns {void}
   */
  function setCachedMeta(item, meta) {
    var key = getSelectionCacheKey(item);
    if (!key) {
      return;
    }
    selectionMetaCache.set(key, meta);
  }

  /**
   * @param {string} message
   * @returns {void}
   */
  function showMessage(message) {
    window.alert(message);
  }

  /**
   * @param {Array<{ id: string, url: string }>} selection
   * @returns {string | null}
   */
  function getSelectionRootTypeFromCacheOrDom(selection) {
    var rootType = null;
    if (selection.length > 0) {
      for (var index = 0; index < selection.length; index += 1) {
        var selectedItem = selection[index];
        var cachedMeta = getCachedMeta(selectedItem);
        if (cachedMeta && cachedMeta.type) {
          rootType = cachedMeta.type;
          break;
        }
      }
    }

    if (!rootType) {
      var controls = getControls();
      for (var controlIndex = 0; controlIndex < controls.length; controlIndex += 1) {
        var controlProduct = controls[controlIndex].product;
        var hasMatch = selection.some(function (item) {
          return (item.id && controlProduct.id && item.id === controlProduct.id) || (item.url && item.url === controlProduct.url);
        });
        if (hasMatch && controlProduct.type) {
          rootType = controlProduct.type;
          break;
        }
      }
    }

    return rootType;
  }

  /**
   * @param {Array<{ id: string, url: string }>} selection
   * @returns {Promise<{ type: string | null, validated: boolean }>}
   */
  async function getSelectionRootType(selection) {
    var cachedType = getSelectionRootTypeFromCacheOrDom(selection);
    if (cachedType || !selection.length) {
      return {
        type: cachedType,
        validated: true,
      };
    }

    var firstItem = selection[0];
    var resolved = await resolveProductPayloadResult(firstItem);
    if (resolved.entry) {
      return {
        type: resolved.entry.payload.type || null,
        validated: true,
      };
    }

    return {
      type: null,
      validated: Boolean(resolved.shouldRemove),
    };
  }

  /**
   * @param {Array<{ id: string, url: string }>} selection
   * @param {{ id: string, url: string, type: string, title: string, image: string }} product
   * @param {boolean} shouldAdd
   * @returns {Promise<{ nextSelection: Array<{ id: string, url: string }>, message: string | null }>}
   */
  async function updateSelection(selection, product, shouldAdd) {
    if (!shouldAdd) {
      var filtered = selection.filter(function (item) {
        return !(item.id && product.id && item.id === product.id) && !(item.url && product.url && item.url === product.url);
      });
      return {
        nextSelection: filtered,
        message: null,
      };
    }

    selection = await sanitizeSelectionFromSource(selection);

    var hasDuplicate = selection.some(function (item) {
      var sameId = product.id && item.id && item.id === product.id;
      var sameUrl = product.url && item.url && item.url === product.url;
      return sameId || sameUrl;
    });

    if (hasDuplicate) {
      return {
        nextSelection: selection,
        message: getMessages().alreadyAdded,
      };
    }

    var maxItems = getMaxItems();
    if (selection.length >= maxItems) {
      return {
        nextSelection: selection,
        message: getMessages().limitReached,
      };
    }

    var rootTypeResult = await getSelectionRootType(selection);
    if (!rootTypeResult.validated) {
      return {
        nextSelection: selection,
        message: getMessages().refreshFailed,
      };
    }

    if (rootTypeResult.type && product.type !== rootTypeResult.type) {
      return {
        nextSelection: selection,
        message: getMessages().typeMismatchTemplate.replace("__TYPE__", rootTypeResult.type),
      };
    }

    var next = selection.concat([
      {
        id: product.id,
        url: product.url,
      },
    ]);
    return {
      nextSelection: next,
      message: null,
    };
  }

  /**
   * @returns {Array<{ container: Element, input: HTMLInputElement, product: { id: string, url: string, type: string, title: string, image: string } }>}
   */
  function getControls() {
    /** @type {Array<{ container: Element, input: HTMLInputElement, product: { id: string, url: string, type: string, title: string, image: string } }>} */
    var controls = [];
    var nodes = document.querySelectorAll(".twc-product-compare-toggle, .custom-confront-button");

    nodes.forEach(function (node) {
      if (!(node instanceof Element)) {
        return;
      }

      var input = node.querySelector('input[type="checkbox"]');
      if (!(input instanceof HTMLInputElement)) {
        return;
      }

      controls.push({
        container: node,
        input: input,
        product: readProductFromNode(node),
      });
    });

    return controls;
  }

  /**
   * @param {Array<{ id: string, url: string }>} selection
   * @returns {void}
   */
  function syncControls(selection) {
    var controls = getControls();

    controls.forEach(function (control) {
      var isSelected = selection.some(function (item) {
        var sameId = control.product.id && item.id && control.product.id === item.id;
        var sameUrl = control.product.url && item.url && control.product.url === item.url;
        return sameId || sameUrl;
      });
      control.input.checked = isSelected;
    });
  }

  /**
   * @param {Array<{ id: string, url: string, type: string, title: string, image: string }>} selection
   * @returns {void}
   */
  function updateLauncher(selection) {
    if (!launcherElement || !launcherShellElement) {
      return;
    }

    var count = selection.length;
    var minReached = count >= MIN_COMPARE_ITEMS;
    var countElement = launcherElement.querySelector("[data-compare-count]");
    launcherShellElement.hidden = count === 0;
    window.dispatchEvent(new CustomEvent('custom:compare:change'));
    launcherElement.dataset.enabled = minReached ? "true" : "false";
    if (countElement) {
      countElement.textContent = String(count);
    }
  }

  /**
   * @returns {void}
   */
  function ensureShell() {
    if (launcherShellElement && launcherElement && modalElement && tableHostElement && bodyElement) {
      return;
    }

    var launcherShell = document.createElement("div");
    launcherShell.className = "twc-compare-launcher-shell";
    launcherShell.hidden = true;

    var launcher = document.createElement("button");
    launcher.type = "button";
    launcher.className = "twc-compare-launcher";
    launcher.setAttribute("aria-haspopup", "dialog");
    launcher.innerHTML =
      '<span class="twc-compare-launcher__label">' +
      escapeHtml(getMessages().launcherLabel) +
      "</span>" +
      '<span class="twc-compare-launcher__count" data-compare-count>0</span>';

    var launcherClear = document.createElement("button");
    launcherClear.type = "button";
    launcherClear.className = "twc-compare-launcher-clear";
    launcherClear.setAttribute("aria-label", getMessages().clear || "");
    launcherClear.setAttribute("title", getMessages().clear || "");
    launcherClear.setAttribute("data-compare-clear", "");
    launcherClear.innerHTML =
      '<svg aria-hidden="true" focusable="false" viewBox="0 0 20 20">' +
      '<path d="M7.25 2.75A1.75 1.75 0 0 1 9 1h2a1.75 1.75 0 0 1 1.75 1.75V3.5H16a.75.75 0 0 1 0 1.5h-.82l-.62 10.52A2.75 2.75 0 0 1 11.81 18H8.19a2.75 2.75 0 0 1-2.75-2.48L4.82 5H4a.75.75 0 0 1 0-1.5h3.25v-.75Zm1.5.75h2.5v-.75A.25.25 0 0 0 11 2.5H9a.25.25 0 0 0-.25.25v.75Zm-2.43 1.5.61 10.43a1.25 1.25 0 0 0 1.26 1.07h3.62a1.25 1.25 0 0 0 1.26-1.07L13.68 5H6.32Z"/>' +
      "</svg>";

    var modal = document.createElement("dialog");
    modal.className = "twc-compare-modal";
    modal.innerHTML =
      '<form method="dialog" class="twc-compare-modal__chrome">' +
      '<button type="submit" class="twc-compare-modal__close" aria-label="' +
      escapeAttribute(getMessages().close) +
      '">×</button>' +
      "</form>" +
      '<div class="twc-compare-modal__header">' +
      '<p class="twc-compare-modal__eyebrow">' +
      escapeHtml(getMessages().modalEyebrow) +
      "</p>" +
      "<h2>" +
      escapeHtml(getMessages().modalTitle) +
      "</h2>" +
      "</div>" +
      '<div class="twc-compare-modal__actions">' +
      '<button type="button" class="button button-secondary twc-compare-action" data-compare-clear>' +
      escapeHtml(getMessages().clear) +
      "</button>" +
      '<div class="twc-compare-actions-summary-viewport" data-compare-actions-summary-viewport hidden>' +
      '<div class="twc-compare-actions-summary" data-compare-actions-summary></div>' +
      "</div>" +
      "</div>" +
      '<div class="twc-compare-modal__body" data-compare-table-host></div>';

    launcherShell.appendChild(launcher);
    launcherShell.appendChild(launcherClear);
    document.body.appendChild(launcherShell);
    var ringWidget = document.querySelector('[data-ring-widget]');
    if (ringWidget) {
      var dock = document.createElement('div');
      dock.className = 'twc-selection-dock';
      document.body.appendChild(dock);
      dock.appendChild(ringWidget);
      dock.appendChild(launcherShell);
    }
    document.body.appendChild(modal);

    launcherShellElement = launcherShell;
    launcherElement = launcher;
    modalElement = modal;
    tableHostElement = modal.querySelector("[data-compare-table-host]");
    bodyElement = document.body;
  }

  /**
   * @returns {{ name: string, value: string, comparableValue: number | null, comparisonDirection: string | null, isComparable: boolean }[]}
   */
  function normalizeProperties(properties) {
    if (!Array.isArray(properties)) {
      return [];
    }

    return properties
      .filter(function (property) {
        return property && typeof property === "object" && typeof property.name === "string";
      })
      .map(function (property) {
        var comparableValue =
          typeof property.comparable_value === "number" && Number.isFinite(property.comparable_value)
            ? property.comparable_value
            : null;
        var isComparable = Boolean(property.is_comparable) && comparableValue !== null;

        return {
          name: property.name,
          subtitle: typeof property.subtitle === "string" ? property.subtitle : "",
          value: typeof property.value === "string" ? property.value : "",
          comparableValue: comparableValue,
          comparisonDirection: typeof property.comparison_direction === "string" ? property.comparison_direction : null,
          isComparable: isComparable,
        };
      });
  }

  /**
   * @param {string} url
   * @returns {Promise<{ payload: null | { product_name: string, customer_id_product?: string, type: string, id: number | string, img?: string, price_min?: number, price_max?: number, properties?: unknown[] }, finalUrl: string, shouldRemove: boolean, productData: { title: string, options: string[], variants: Array<{ id: string, title: string, available: boolean, selected: boolean }> } | null }>}
   */
  async function fetchCompareJson(url) {
    try {
      var response = await window.fetch(url, { credentials: "same-origin" });
      var finalUrl = normalizeUrl(response.url || url) || url;
      if (hasProductComparisonRedirect(url, finalUrl)) {
        return {
          payload: null,
          finalUrl: finalUrl,
          shouldRemove: true,
          productData: null,
        };
      }

      if (response.status === 404) {
        return {
          payload: null,
          finalUrl: finalUrl,
          shouldRemove: true,
          productData: null,
        };
      }

      if (!response.ok) {
        return {
          payload: null,
          finalUrl: finalUrl,
          shouldRemove: false,
          productData: null,
        };
      }

      var text = await response.text();
      var parser = new window.DOMParser();
      var doc = parser.parseFromString(text, "text/html");
      var script = doc.querySelector("#product-compare-json");
      if (!script || !script.textContent) {
        return {
          payload: null,
          finalUrl: finalUrl,
          shouldRemove: true,
          productData: null,
        };
      }

      var productData = normalizeProductData(await fetchProductJson(finalUrl), finalUrl);

      try {
        return {
          payload: JSON.parse(script.textContent),
          finalUrl: finalUrl,
          shouldRemove: false,
          productData: productData,
        };
      } catch (_jsonError) {
        return {
          payload: null,
          finalUrl: finalUrl,
          shouldRemove: true,
          productData: productData,
        };
      }
    } catch (_error) {
      return {
        payload: null,
        finalUrl: url,
        shouldRemove: false,
        productData: null,
      };
    }
  }

  /**
   * @param {unknown} item
   * @returns {{ id: string, url: string, currentPage: boolean } | null}
   */
  function normalizeCompareListItem(item) {
    if (!item || typeof item !== "object") {
      return null;
    }

    var product = /** @type {{ id?: unknown, url?: unknown, current?: unknown, currentPage?: unknown, type?: unknown, title?: unknown, image?: unknown }} */ (item);
    var normalizedUrl = normalizeUrl(typeof product.url === "string" ? product.url : "");
    var normalizedId = typeof product.id === "number" || typeof product.id === "string" ? String(product.id) : "";

    if (!normalizedUrl) {
      return null;
    }

    var selectionItem = {
      id: normalizedId,
      url: normalizedUrl,
      currentPage: Boolean(product.current || product.currentPage),
    };

    setCachedMeta(selectionItem, {
      type: typeof product.type === "string" && product.type.trim() ? product.type.trim() : "generic",
      title: typeof product.title === "string" ? product.title.trim() : "",
      image: typeof product.image === "string" ? product.image.trim() : "",
    });

    return selectionItem;
  }

  /**
   * @param {Element} root
   * @returns {Array<{ id: string, url: string, currentPage: boolean }>}
   */
  function readCompareListFromSection(root) {
    var script = root.querySelector("[data-custom-related-compare-products]");
    if (!(script instanceof HTMLScriptElement) || !script.textContent) {
      return [];
    }

    try {
      var payload = JSON.parse(script.textContent);
      var products = payload && typeof payload === "object" && Array.isArray(payload.products) ? payload.products : [];
      /** @type {Set<string>} */
      var seen = new Set();
      /** @type {Array<{ id: string, url: string, currentPage: boolean }>} */
      var normalized = [];

      products.forEach(function (product) {
        var item = normalizeCompareListItem(product);
        if (!item) {
          return;
        }

        var key = item.id ? "id:" + item.id : "url:" + item.url;
        if (seen.has(key)) {
          return;
        }

        seen.add(key);
        if (item.currentPage) {
          normalized.unshift(item);
        } else {
          normalized.push(item);
        }
      });

      return normalized;
    } catch (_error) {
      return [];
    }
  }

  /**
   * @param {unknown} payload
   * @returns {payload is { product_name: string, customer_id_product?: string, type: string, id: number | string, img?: string, price_min?: number, price_max?: number, properties?: unknown[] }}
   */
  function isValidComparePayload(payload) {
    return (
      Boolean(payload) &&
      typeof payload === "object" &&
      typeof payload.product_name === "string" &&
      typeof payload.type === "string" &&
      (typeof payload.id === "string" || typeof payload.id === "number")
    );
  }

  /**
   * @param {{ id: string, url: string }} selected
   * @returns {Promise<{ entry: null | { selected: { id: string, url: string, type: string, title: string, image: string }, payload: { product_name: string, customer_id_product?: string, type: string, id: number | string, img?: string, price_min?: number, price_max?: number, properties?: unknown[] }, productData: { title: string, options: string[], variants: Array<{ id: string, title: string, available: boolean, selected: boolean }> } | null }, shouldRemove: boolean }>}
   */
  async function resolveProductPayloadResult(selected) {
    var inlineScript = document.querySelector("#product-compare-json");
    var isCurrentPage = normalizeUrl(window.location.pathname + window.location.search) === selected.url;
    var cachedMeta = getCachedMeta(selected);
    /** @type {{ id: string, url: string, type: string, title: string, image: string }} */
    var selectedView = {
      id: selected.id,
      url: selected.url,
      currentPage: Boolean(selected.currentPage),
      type: (cachedMeta && cachedMeta.type) || "generic",
      title: (cachedMeta && cachedMeta.title) || "",
      image: (cachedMeta && cachedMeta.image) || "",
    };

    if (inlineScript && inlineScript.textContent && isCurrentPage) {
      try {
        var inlinePayload = JSON.parse(inlineScript.textContent);
        if (!isValidComparePayload(inlinePayload)) {
          return {
            entry: null,
            shouldRemove: true,
          };
        }
        setCachedMeta(selected, {
          type: inlinePayload.type,
          title: inlinePayload.product_name,
          image: inlinePayload.img || "",
        });
        var inlineProductData = normalizeProductData(await fetchProductJson(selectedView.url), selectedView.url);
        return {
          entry: {
            selected: {
              id: String(inlinePayload.id || selectedView.id),
              url: selectedView.url,
              currentPage: selectedView.currentPage,
              type: inlinePayload.type || selectedView.type,
              title: inlinePayload.product_name || selectedView.title,
              image: inlinePayload.img || selectedView.image,
            },
            payload: inlinePayload,
            productData: inlineProductData,
          },
          shouldRemove: false,
        };
      } catch (_error) {
        return {
          entry: null,
          shouldRemove: true,
        };
      }
    }

    var fetched = await fetchCompareJson(selected.url);
    if (!fetched.payload || !isValidComparePayload(fetched.payload)) {
      return {
        entry: null,
        shouldRemove: fetched.shouldRemove || Boolean(fetched.payload),
      };
    }
    setCachedMeta(selected, {
      type: fetched.payload.type,
      title: fetched.payload.product_name,
      image: fetched.payload.img || "",
    });

    return {
      entry: {
        selected: {
          id: String(fetched.payload.id || selectedView.id),
          url: fetched.finalUrl || selectedView.url,
          currentPage: selectedView.currentPage,
          type: fetched.payload.type || selectedView.type,
          title: fetched.payload.product_name || selectedView.title,
          image: fetched.payload.img || selectedView.image,
        },
        payload: fetched.payload,
        productData: fetched.productData,
      },
      shouldRemove: false,
    };
  }

  /**
   * @param {{ id: string, url: string }} selected
   * @returns {Promise<null | { selected: { id: string, url: string, type: string, title: string, image: string }, payload: { product_name: string, customer_id_product?: string, type: string, id: number | string, img?: string, price_min?: number, price_max?: number, properties?: unknown[] }, productData: { title: string, options: string[], variants: Array<{ id: string, title: string, available: boolean, selected: boolean }> } | null }>}
   */
  async function resolveProductPayload(selected) {
    var result = await resolveProductPayloadResult(selected);
    return result.entry;
  }

  /**
   * @param {Array<{ id: string, url: string }>} selection
   * @returns {Promise<Array<{ id: string, url: string }>>}
   */
  async function sanitizeSelectionFromSource(selection) {
    if (!selection.length) {
      return [];
    }

    var resolved = await Promise.all(
      selection.map(function (item) {
        return resolveProductPayloadResult(item);
      }),
    );

    return resolved
      .filter(function (result) {
        return Boolean(result.entry || !result.shouldRemove);
      })
      .map(function (result, index) {
        var fallbackItem = selection[index] || { id: "", url: "" };
        var entry = result.entry;
        return {
          id: entry ? entry.selected.id : fallbackItem.id,
          url: entry ? entry.selected.url : fallbackItem.url,
        };
      });
  }

  /**
   * @param {Array<{ selected: { id: string, url: string, type: string, title: string, image: string }, payload: { product_name: string, customer_id_product?: string, type: string, id: number | string, img?: string, price_min?: number, properties?: unknown[] }, productData: { title: string, options: string[], variants: Array<{ id: string, title: string, available: boolean, selected: boolean }> } | null }>} products
   * @returns {Array<{ key: string, label: string, subtitle: string, values: Array<{ htmlValue: string, comparableValue: number | null, rank: number | null }>, isComparable: boolean, comparisonDirection: "high_is_better" | "low_is_better" | null, bestValue: number | null }>}
   */
  function buildRows(products) {
    /** @type {Map<string, { label: string, subtitle: string, isSystemPrice: boolean, byProduct: Array<{ htmlValue: string, missing: boolean, comparableValue: number | null, isComparable: boolean, comparisonDirection: "high_is_better" | "low_is_better" | null }> }>} */
    var rowMap = new Map();

    rowMap.set("__price_min__", {
      label: getMessages().priceFrom,
      subtitle: "",
      isSystemPrice: true,
      byProduct: products.map(function (entry) {
        var maybePrice = typeof entry.payload.price_min === "number" ? entry.payload.price_min : null;
        var maybePriceMax = typeof entry.payload.price_max === "number" ? entry.payload.price_max : maybePrice;
        var hasRange = maybePrice !== null && maybePriceMax !== null && maybePrice !== maybePriceMax;
        var displayPrice = "—";

        if (maybePrice !== null && maybePriceMax !== null) {
          displayPrice = hasRange
            ? "\u20AC " + String(maybePrice) + " - \u20AC " + String(maybePriceMax)
            : "\u20AC " + String(maybePrice);
        }

        return {
          htmlValue: escapeHtml(displayPrice),
          missing: maybePrice === null,
          comparableValue: maybePrice,
          isComparable: maybePrice !== null,
          comparisonDirection: "low_is_better",
        };
      }),
    });

    products.forEach(function (entry, productIndex) {
      normalizeProperties(entry.payload.properties).forEach(function (property) {
        var key = property.name.trim().toLowerCase();
        if (!key) {
          return;
        }

        if (!rowMap.has(key)) {
          rowMap.set(key, {
            label: property.name,
            subtitle: property.subtitle || "",
            isSystemPrice: false,
            byProduct: products.map(function () {
              return {
                htmlValue: "",
                missing: true,
                comparableValue: null,
                isComparable: false,
                comparisonDirection: null,
              };
            }),
          });
        }

        var row = rowMap.get(key);
        row.byProduct[productIndex] = {
          htmlValue: property.value || "\u2014",
          missing: false,
          comparableValue: property.comparableValue,
          isComparable: property.isComparable,
          comparisonDirection:
            property.comparisonDirection === "high_is_better" || property.comparisonDirection === "low_is_better"
              ? property.comparisonDirection
              : null,
        };
      });
    });

    /** @type {Array<{ key: string, label: string, subtitle: string, values: Array<{ htmlValue: string, comparableValue: number | null, rank: number | null }>, isComparable: boolean, comparisonDirection: "high_is_better" | "low_is_better" | null, bestValue: number | null }>} */
    var rows = [];

    rowMap.forEach(function (row, key) {
      var comparableCells = row.byProduct.filter(function (cell) {
        return (
          cell.isComparable &&
          cell.comparableValue !== null &&
          (cell.comparisonDirection === "high_is_better" || cell.comparisonDirection === "low_is_better")
        );
      });

      var direction = comparableCells.length > 0 ? comparableCells[0].comparisonDirection : null;
      var hasMixedDirections = comparableCells.some(function (cell) {
        return cell.comparisonDirection !== direction;
      });
      var isComparable = !hasMixedDirections && direction !== null && comparableCells.length > 0;

      /** @type {number | null} */
      var bestValue = null;
      /** @type {Map<number, number>} */
      var rankByValue = new Map();
      if (isComparable && direction) {
        var numericValues = comparableCells.map(function (cell) {
          return cell.comparableValue;
        });
        bestValue = direction === "high_is_better" ? Math.max.apply(null, numericValues) : Math.min.apply(null, numericValues);

        var uniqueSortedValues = Array.from(
          new Set(
            numericValues.filter(function (value) {
              return typeof value === "number";
            }),
          ),
        ).sort(function (a, b) {
          return direction === "high_is_better" ? b - a : a - b;
        });
        uniqueSortedValues.forEach(function (value, index) {
          rankByValue.set(value, index + 1);
        });
      }

      rows.push({
        key: key,
        label: row.label,
        subtitle: row.subtitle,
        values: row.byProduct.map(function (cell) {
          var rank =
            isComparable &&
            cell.isComparable &&
            cell.comparableValue !== null &&
            rankByValue.has(cell.comparableValue)
              ? rankByValue.get(cell.comparableValue) || null
              : null;
          return {
            htmlValue: !row.isSystemPrice && cell.missing ? "Non presente" : cell.htmlValue,
            comparableValue: cell.comparableValue,
            rank: rank,
          };
        }),
        isComparable: isComparable,
        comparisonDirection: direction,
        bestValue: bestValue,
      });
    });

    var comparableRows = rows.filter(function (row) {
      return row.isComparable;
    });

    /**
     * @param {number | null} value
     * @returns {string}
     */
    function formatSummaryEuroPrice(value) {
      if (typeof value !== "number" || !Number.isFinite(value)) {
        return "—";
      }

      var rounded = Math.round((value + Number.EPSILON) * 100) / 100;
      return new Intl.NumberFormat("it-IT", {
        style: "currency",
        currency: "EUR",
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(rounded);
    }

    if (comparableRows.length > 0) {
      rows.push({
        key: "__summary__",
        label: "Riepilogo",
        subtitle: "",
        values: products.map(function (_entry, productIndex) {
          var parts = comparableRows
            .map(function (row) {
              var cellValue = row.values[productIndex] || null;
              var rank = cellValue ? cellValue.rank : null;
              if (rank === null) {
                return null;
              }

              var priceSuffix = "";
              if (row.key === "__price_min__") {
                priceSuffix =
                  " (" + escapeHtml(formatSummaryEuroPrice(cellValue ? cellValue.comparableValue : null)) + ")";
              }

              return (
                escapeHtml(row.label) +
                ': <span class="twc-compare-rank twc-compare-rank--' +
                rank +
                '">' +
                rank +
                "</span>" +
                priceSuffix
              );
            })
            .filter(function (value) {
              return value !== null;
            });

          return {
            htmlValue: parts.length ? parts.join("<br>") : "Non disponibile",
            comparableValue: null,
            rank: null,
          };
        }),
        isComparable: false,
        comparisonDirection: null,
        bestValue: null,
      });
    }

    return rows;
  }

  /**
   * @param {{ productData: { title: string, options: string[], variants: Array<{ id: string, title: string, available: boolean, selected: boolean }> } | null }} entry
   * @returns {null | { id: string, title: string, available: boolean, selected: boolean }}
   */
  function getSelectedProductVariant(entry) {
    var variants = entry.productData && Array.isArray(entry.productData.variants) ? entry.productData.variants : [];
    if (!variants.length) {
      return null;
    }

    for (var index = 0; index < variants.length; index += 1) {
      if (variants[index].selected) {
        return variants[index];
      }
    }

    return variants[0] || null;
  }

  /**
   * @param {{ selected: { id: string, url: string }, payload: { product_name: string, id: number | string }, productData: { title: string, options: string[], variants: Array<{ id: string, title: string, available: boolean, selected: boolean }> } | null }} entry
   * @returns {string}
   */
  function renderDesktopProductActions(entry) {
    var variants = entry.productData && Array.isArray(entry.productData.variants) ? entry.productData.variants : [];
    var selectedVariant = getSelectedProductVariant(entry);
    var multipleVariants = variants.length > 1;
    var hideViewProduct = activeCompareSource === "transient" && Boolean(entry.selected.currentPage);
    var optionLabel =
      entry.productData && entry.productData.options && entry.productData.options.length
        ? entry.productData.options.join(" / ")
        : "Variante";
    var variantSelectMarkup = "";

    if (multipleVariants) {
      variantSelectMarkup =
        '<label class="twc-compare-product-actions__label" for="compare-variant-' +
        escapeAttribute(entry.selected.id) +
        '">' +
        escapeHtml(optionLabel) +
        "</label>" +
        '<div class="twc-compare-product-actions__select-wrap">' +
        '<select class="twc-compare-product-actions__select" id="compare-variant-' +
        escapeAttribute(entry.selected.id) +
        '" data-compare-variant-select data-product-id="' +
        escapeAttribute(entry.selected.id) +
        '">' +
        variants
          .map(function (variant) {
            return (
              '<option value="' +
              escapeAttribute(variant.id) +
              '" data-available="' +
              escapeAttribute(String(variant.available)) +
              '"' +
              (variant.selected ? ' selected="selected"' : "") +
              ">" +
              escapeHtml(
                variant.available ? variant.title : variant.title + " - " + getMessages().unavailable,
              ) +
              "</option>"
            );
          })
          .join("") +
        "</select>" +
        "</div>";
    }

    return (
      '<div class="twc-compare-product-actions">' +
      (hideViewProduct
        ? ""
        : '<a href="' +
          escapeAttribute(entry.selected.url) +
          '" class="button button-secondary twc-compare-product-actions__button twc-compare-product-actions__button--view">' +
          escapeHtml(getMessages().viewProduct) +
          "</a>") +
      variantSelectMarkup +
      (window.CustomShopify.RingBuilder && selectedVariant ?
        '<div data-ring-compare-url="' + escapeAttribute(entry.selected.url) +
        '" data-ring-compare-product="' + escapeAttribute(entry.selected.id) +
        '" data-ring-compare-variant="' + escapeAttribute(selectedVariant.id) + '"></div>' : '') +
      '<button type="button" class="button twc-compare-product-actions__button twc-compare-product-actions__button--cart" data-compare-add-to-cart data-product-id="' +
      escapeAttribute(entry.selected.id) +
      '" data-product-url="' +
      escapeAttribute(entry.selected.url) +
      '" data-variant-id="' +
      escapeAttribute(selectedVariant ? selectedVariant.id : "") +
      '"' +
      (selectedVariant && selectedVariant.available ? "" : " disabled") +
      ">" +
      escapeHtml(selectedVariant && selectedVariant.available ? getMessages().addToCart : getMessages().unavailable) +
      "</button>" +
      '<button type="button" class="button button-secondary twc-compare-product-actions__button twc-compare-product-actions__button--buy-now" data-compare-buy-now data-product-id="' +
      escapeAttribute(entry.selected.id) +
      '" data-variant-id="' +
      escapeAttribute(selectedVariant ? selectedVariant.id : "") +
      '"' +
      (selectedVariant && selectedVariant.available ? "" : " disabled") +
      ">" +
      escapeHtml(selectedVariant && selectedVariant.available ? getMessages().buyNow : getMessages().unavailable) +
      "</button>" +
      "</div>"
    );
  }

  /**
   * @param {string} htmlValue
   * @returns {string[]}
   */
  function getSummaryParts(htmlValue) {
    if (typeof htmlValue !== "string" || !htmlValue) {
      return [];
    }

    return htmlValue
      .split("<br>")
      .map(function (part) {
        return part.trim();
      })
      .filter(function (part) {
        return Boolean(part);
      });
  }

  /**
   * @param {{ selected: { url: string, title: string, image: string }, payload: { product_name: string, customer_id_product?: string, id: number | string, img?: string }, productData: { title: string, options: string[], variants: Array<{ id: string, title: string, available: boolean, selected: boolean }> } | null }} entry
   * @param {{ htmlValue: string }} value
   * @returns {string}
   */
  function renderMobileSummaryCard(entry, value) {
    var title = entry.payload.product_name || entry.selected.title || "Prodotto";
    var productCode =
      typeof entry.payload.customer_id_product === "string" && entry.payload.customer_id_product.trim()
        ? entry.payload.customer_id_product.trim()
        : String(entry.payload.id || "");
    var productImage = entry.payload.img || entry.selected.image || "";
    var summaryParts = getSummaryParts(value.htmlValue);

    return (
      '<li class="twc-compare-mobile-row__value twc-compare-mobile-row__value--summary">' +
      '<div class="twc-compare-mobile-summary">' +
      renderCurrentProductBadge(entry) +
      (productImage
        ? '<img class="twc-compare-mobile-summary__image" src="' +
          escapeAttribute(productImage) +
          '" alt="' +
          escapeAttribute(title) +
          '" loading="lazy">'
        : "") +
      '<a class="twc-compare-mobile-summary__title" href="' +
      escapeAttribute(entry.selected.url) +
      '">' +
      escapeHtml(title) +
      "</a>" +
      (productCode ? '<p class="twc-compare-mobile-summary__code">Codice: ' + escapeHtml(productCode) + "</p>" : "") +
      '<div class="twc-compare-mobile-summary__points">' +
      (summaryParts.length
        ? summaryParts
            .map(function (part) {
              return '<span class="twc-compare-mobile-summary__point">' + part + "</span>";
            })
            .join("")
        : '<span class="twc-compare-mobile-summary__point">' + escapeHtml(value.htmlValue || "Non disponibile") + "</span>") +
      "</div>" +
      '<div class="twc-compare-mobile-summary__actions">' +
      renderDesktopProductActions(entry) +
      "</div>" +
      "</div>" +
      "</li>"
    );
  }

  /**
   * @param {number | null} rank
   * @returns {string}
   */
  function renderRankBadge(rank) {
    if (rank === null || rank < 1) {
      return "";
    }
    return '<span class="twc-compare-rank twc-compare-rank--' + rank + '">' + String(rank) + "</span>";
  }

  /**
   * @param {{ htmlValue: string, rank: number | null }} value
   * @returns {string}
   */
  function renderValueWithRank(value) {
    var rankBadge = renderRankBadge(value.rank);
    if (!rankBadge) {
      return value.htmlValue;
    }

    return (
      '<span class="twc-compare-value-with-rank">' +
      rankBadge +
      '<span class="twc-compare-value-with-rank__content">' +
      value.htmlValue +
      "</span>" +
      "</span>"
    );
  }

  /**
   * @param {string} url
   * @param {number} sourceSize
   * @returns {string}
   */
  function normalize360Url(url, sourceSize) {
    if (typeof url !== "string") {
      return "";
    }

    var raw = url.trim();
    if (!raw) {
      return "";
    }

    var normalized = raw.replace(/^http:\/\//i, "https://");
    try {
      var parsedUrl = new URL(normalized, window.location.origin);
      if (parsedUrl.protocol !== "https:") {
        return "";
      }
      normalized = parsedUrl.toString();
    } catch (_error) {
      return "";
    }

    var hasVideoPattern = /\/video\/\d+\/\d+(?:\/autoplay)?(?:\?|$)/.test(normalized);
    if (!hasVideoPattern) {
      return "";
    }

    var embedSize = Number.isFinite(sourceSize) && sourceSize > 0 ? Math.round(sourceSize) : 560;
    var withEmbedSize = normalized.replace(
      /\/video\/\d+\/\d+(?=\/autoplay|\?|$)/,
      "/video/" + embedSize + "/" + embedSize,
    );

    var withAutoplay = withEmbedSize;
    if (withAutoplay.indexOf("/autoplay") === -1) {
      withAutoplay = withAutoplay.replace(
        /\/video\/\d+\/\d+(?=\?|$)/,
        function (match) {
          return match + "/autoplay";
        },
      );
    }

    return withAutoplay;
  }

  /**
   * @param {string} maybeType
   * @returns {boolean}
   */
  function isDiamondType(maybeType) {
    return typeof maybeType === "string" && /diam/i.test(maybeType);
  }

  /**
   * @param {{ payload: { product_name: string, diamond_video_url?: string, type?: string, img?: string }, selected: { image: string } }} entry
   * @param {boolean} useInlineTrigger
   * @param {string} productCode
   * @returns {string}
   */
  function renderCompareHeaderMedia(entry, useInlineTrigger, productCode) {
    var image = entry.payload.img || entry.selected.image || "";
    var title = entry.payload.product_name || "Prodotto";
    var sourceSize = 720;
    var videoUrl = normalize360Url(entry.payload.diamond_video_url || "", sourceSize);
    var shouldRender360 = isDiamondType(entry.payload.type || "") && videoUrl;

    if (shouldRender360 && useInlineTrigger) {
      return (
        '<div class="twc-compare-header-media-wrap twc-compare-header-media-wrap--360-trigger">' +
        '<div class="twc-compare-header-media">' +
        (image
          ? '<img src="' + escapeAttribute(image) + '" alt="' + escapeAttribute(title) + '" loading="lazy">'
          : '<span class="twc-compare-header-media__empty-fill" aria-hidden="true"></span>') +
        '<button type="button" class="twc-compare-header-media__360-trigger" data-compare-360-open data-compare-360-url="' +
        escapeAttribute(videoUrl) +
        '" data-compare-360-title="' +
        escapeAttribute(title) +
        '" data-compare-360-code="' +
        escapeAttribute(productCode) +
        '">360&deg;</button>' +
        "</div>" +
        "</div>"
      );
    }

    if (shouldRender360) {
      return (
        '<div class="twc-compare-header-media-wrap twc-compare-header-media-wrap--360">' +
        '<div class="twc-compare-header-media twc-compare-header-media--360" data-compare-360-media data-compare-360-source-size="' +
        String(sourceSize) +
        '">' +
        '<iframe class="twc-compare-header-media__iframe" loading="lazy" scrolling="no" allow="autoplay; fullscreen; picture-in-picture" referrerpolicy="strict-origin-when-cross-origin" src="' +
        escapeAttribute(videoUrl) +
        '" title="' +
        escapeAttribute(title) +
        '"></iframe>' +
        "</div>" +
        '<p class="twc-compare-header-media__hint">Puoi girare il diamante cliccando col mouse o toccandolo.</p>' +
        "</div>"
      );
    }

    if (image) {
      return (
        '<div class="twc-compare-header-media">' +
        '<img src="' + escapeAttribute(image) + '" alt="' + escapeAttribute(title) + '" loading="lazy">' +
        "</div>"
      );
    }

    return '<div class="twc-compare-header-media twc-compare-header-media--empty" aria-hidden="true"></div>';
  }

  /**
   * @param {HTMLElement} node
   * @returns {void}
   */
  function sizeCompare360MediaNode(node) {
    var sourceSize = Number.parseInt(node.getAttribute("data-compare-360-source-size") || "560", 10);
    if (!Number.isFinite(sourceSize) || sourceSize <= 0) {
      sourceSize = 560;
    }

    var displaySize = Math.max(1, Math.round(node.getBoundingClientRect().width));
    node.style.setProperty("--twc-compare-360-source-size", String(sourceSize) + "px");
    node.style.setProperty("--twc-compare-360-scale", String(displaySize / sourceSize));
  }

  /**
   * @returns {void}
   */
  function setupCompare360Media() {
    if (!tableHostElement) {
      return;
    }

    var mediaNodes = tableHostElement.querySelectorAll("[data-compare-360-media]");
    mediaNodes.forEach(function (node) {
      if (node instanceof HTMLElement) {
        sizeCompare360MediaNode(node);
      }
    });
  }

  /**
   * @param {string} url
   * @param {string} title
   * @param {string} code
   * @returns {void}
   */
  function showInlineCompare360Viewer(url, title, code) {
    if (!tableHostElement || !url) {
      return;
    }

    var iframe = tableHostElement.querySelector("[data-compare-inline-360-iframe]");
    var titleElement = tableHostElement.querySelector("[data-compare-inline-360-title]");
    var codeElement = tableHostElement.querySelector("[data-compare-inline-360-code]");
    var mediaElement = tableHostElement.querySelector("[data-compare-inline-360-media]");
    if (!(iframe instanceof HTMLIFrameElement) || !(mediaElement instanceof HTMLElement)) {
      return;
    }

    iframe.title = title;
    iframe.src = url;
    mediaElement.setAttribute("data-active", "true");
    if (titleElement instanceof HTMLElement) {
      titleElement.textContent = title;
    }
    if (codeElement instanceof HTMLElement) {
      codeElement.textContent = code ? "Codice: " + code : "";
    }
    requestAnimationFrame(function () {
      sizeCompare360MediaNode(mediaElement);
    });
  }

  /**
   * @param {{ selected: { currentPage?: boolean } }} entry
   * @returns {string}
   */
  function renderCurrentProductBadge(entry) {
    if (!entry.selected.currentPage) {
      return "";
    }

    return '<p class="twc-compare-current-product">Prodotto in pagina</p>';
  }

  /**
   * @param {{ selected: { currentPage?: boolean } }} entry
   * @returns {string}
   */
  function renderCurrentProductBadgeCompact(entry) {
    if (!entry.selected.currentPage) {
      return "";
    }

    return '<p class="twc-compare-current-product">In pagina</p>';
  }

  /**
   * @param {{ selected: { id: string, currentPage?: boolean } }} entry
   * @returns {string}
   */
  function renderCompareRemoveButton(entry) {
    if (entry.selected.currentPage) {
      return "";
    }

    return (
      '<button type="button" class="twc-compare-table__remove" data-compare-remove="' +
      escapeAttribute(entry.selected.id) +
      '">' +
      escapeHtml(getMessages().remove) +
      "</button>"
    );
  }

  /**
   * @param {Array<{ selected: { id: string, url: string, currentPage?: boolean, type: string, title: string, image: string }, payload: { product_name: string, customer_id_product?: string, type: string, id: number | string, img?: string, price_min?: number, properties?: unknown[] }, productData: { title: string, options: string[], variants: Array<{ id: string, title: string, available: boolean, selected: boolean }> } | null }>} products
   * @returns {string}
   */
  function renderDesktop(products) {
    var rows = buildRows(products);
    var headCells = products
      .map(function (entry) {
        var title = entry.payload.product_name || entry.selected.title || "Prodotto";
        var productCode =
          typeof entry.payload.customer_id_product === "string" && entry.payload.customer_id_product.trim()
            ? entry.payload.customer_id_product.trim()
            : String(entry.payload.id || "");

        return (
          '<th scope="col" class="twc-compare-table__product-head' +
          (entry.selected.currentPage ? " twc-compare-table__product-head--current" : "") +
          '">' +
          renderCompareHeaderMedia(entry, true, productCode) +
          renderCurrentProductBadge(entry) +
          '<a href="' +
          escapeAttribute(entry.selected.url) +
          '" class="twc-compare-table__product-title">' +
          escapeHtml(title) +
          "</a>" +
          (productCode ? '<p class="twc-compare-table__product-code">Codice: ' + escapeHtml(productCode) + "</p>" : "") +
          renderCompareRemoveButton(entry) +
          "</th>"
        );
      })
      .join("");

    var bodyRows = rows
      .map(function (row) {
        var cells = row.values
          .map(function (value) {
            var isBest = row.isComparable && row.bestValue !== null && value.comparableValue === row.bestValue;
            var className = isBest ? "twc-compare-table__cell twc-compare-table__cell--best" : "twc-compare-table__cell";
            return '<td class="' + className + '">' + renderValueWithRank(value) + "</td>";
          })
          .join("");

        return (
          "<tr>" +
          '<th scope="row" class="twc-compare-table__row-title">' +
          escapeHtml(row.label) +
          (row.subtitle ? '<span class="twc-compare-table__row-subtitle">' + escapeHtml(row.subtitle) + "</span>" : "") +
          "</th>" +
          cells +
          "</tr>"
        );
      })
      .join("");
    var actionsRow =
      "<tr>" +
      '<th scope="row" class="twc-compare-table__row-title twc-compare-table__row-title--actions">' +
      escapeHtml(getMessages().actionsLabel) +
      "</th>" +
      products
        .map(function (entry) {
          return '<td class="twc-compare-table__cell twc-compare-table__cell--actions">' + renderDesktopProductActions(entry) + "</td>";
        })
        .join("") +
      "</tr>";

    return (
      '<div class="twc-compare-table-wrap">' +
      '<table class="twc-compare-table" aria-label="' + escapeAttribute(getMessages().tableAriaLabel) + '">' +
      '<thead><tr><th class="twc-compare-table__viewer-head">' +
      '<div class="twc-compare-inline-360" data-compare-inline-360>' +
      '<div class="twc-compare-inline-360__media" data-compare-inline-360-media data-compare-360-source-size="720" data-active="false">' +
      '<iframe class="twc-compare-header-media__iframe" data-compare-inline-360-iframe loading="lazy" scrolling="no" allow="autoplay; fullscreen; picture-in-picture" referrerpolicy="strict-origin-when-cross-origin" title=""></iframe>' +
      '<span class="twc-compare-inline-360__placeholder" aria-hidden="true">360&deg;</span>' +
      "</div>" +
      '<p class="twc-compare-inline-360__title" data-compare-inline-360-title></p>' +
      '<p class="twc-compare-inline-360__code" data-compare-inline-360-code></p>' +
      "</div>" +
      "</th>" +
      headCells +
      "</tr></thead>" +
      "<tbody>" +
      bodyRows +
      actionsRow +
      "</tbody>" +
      "</table>" +
      "</div>"
    );
  }

  /**
   * @returns {void}
   */
  function setupDesktopStickyHeaderBehavior(products) {
    if (!tableHostElement) {
      return;
    }

    if (typeof activeDesktopScrollCleanup === "function") {
      activeDesktopScrollCleanup();
      activeDesktopScrollCleanup = null;
    }

    if (window.matchMedia("(max-width: 989px)").matches) {
      return;
    }

    var table = tableHostElement.querySelector(".twc-compare-table");
    var wrap = tableHostElement.querySelector(".twc-compare-table-wrap");
    var summaryViewport = modalElement ? modalElement.querySelector("[data-compare-actions-summary-viewport]") : null;
    var summaryContainer = modalElement ? modalElement.querySelector("[data-compare-actions-summary]") : null;
    if (
      !(table instanceof HTMLTableElement) ||
      !(wrap instanceof HTMLElement) ||
      !(summaryViewport instanceof HTMLElement) ||
      !(summaryContainer instanceof HTMLElement)
    ) {
      return;
    }

    summaryContainer.innerHTML = products
      .map(function (entry) {
        var code =
          typeof entry.payload.customer_id_product === "string" && entry.payload.customer_id_product.trim()
            ? entry.payload.customer_id_product.trim()
            : String(entry.payload.id || "");
        var image = entry.payload.img || entry.selected.image;
        var itemClass =
          "twc-compare-actions-summary__item" + (entry.selected.currentPage ? " twc-compare-actions-summary__item--current" : "");

        return (
          '<div class="' + itemClass + '">' +
          renderCurrentProductBadgeCompact(entry) +
          (image
            ? '<img class="twc-compare-actions-summary__img" src="' +
              escapeAttribute(image) +
              '" alt="' +
              escapeAttribute(entry.payload.product_name || entry.selected.title || "Prodotto") +
              '" loading="lazy">'
            : '<span class="twc-compare-actions-summary__img twc-compare-actions-summary__img--empty" aria-hidden="true"></span>') +
          '<span class="twc-compare-actions-summary__code">' + escapeHtml(code) + "</span>" +
          "</div>"
        );
      })
      .join("");

    var headCells = table.querySelectorAll("thead th");
    if (headCells.length < 2) {
      summaryViewport.hidden = true;
      return;
    }

    var firstColumnWidth = headCells[0].getBoundingClientRect().width;
    var productColumnWidths = [];
    for (var cellIndex = 1; cellIndex < headCells.length; cellIndex += 1) {
      productColumnWidths.push(Math.max(0, headCells[cellIndex].getBoundingClientRect().width));
    }

    summaryViewport.style.marginLeft = String(firstColumnWidth) + "px";
    summaryViewport.style.width = String(Math.max(0, wrap.clientWidth - firstColumnWidth)) + "px";
    summaryContainer.style.gridTemplateColumns = productColumnWidths.map(function (width) {
      return String(width) + "px";
    }).join(" ");

    var scroller = wrap;
    var firstProductMedia = headCells[1].querySelector(".twc-compare-header-media");

    var updateFrozenColumnOffset = function () {
      var scrollLeft = String(scroller.scrollLeft) + "px";
      table.style.setProperty("--twc-compare-scroll-left", scrollLeft);
      summaryContainer.style.transform = "translateX(calc(-1 * " + scrollLeft + "))";
    };

    var updateSummaryVisibility = function () {
      if (!(firstProductMedia instanceof HTMLElement)) {
        summaryViewport.hidden = scroller.scrollTop <= 8;
        return;
      }

      var scrollerRect = scroller.getBoundingClientRect();
      var mediaRect = firstProductMedia.getBoundingClientRect();
      var isMediaVisible = mediaRect.bottom > scrollerRect.top + 8 && mediaRect.top < scrollerRect.bottom - 8;
      summaryViewport.hidden = isMediaVisible;
    };

    var resizeHandler = function () {
      updateFrozenColumnOffset();
      var firstWidth = headCells[0].getBoundingClientRect().width;
      summaryViewport.style.marginLeft = String(firstWidth) + "px";
      summaryViewport.style.width = String(Math.max(0, wrap.clientWidth - firstWidth)) + "px";
      var resizedColumns = [];
      for (var index = 1; index < headCells.length; index += 1) {
        resizedColumns.push(String(Math.max(0, headCells[index].getBoundingClientRect().width)) + "px");
      }
      summaryContainer.style.gridTemplateColumns = resizedColumns.join(" ");
      updateSummaryVisibility();
    };
    var scrollHandler = function () {
      updateFrozenColumnOffset();
      updateSummaryVisibility();
    };

    scroller.addEventListener("scroll", scrollHandler, { passive: true });
    window.addEventListener("resize", resizeHandler);

    activeDesktopScrollCleanup = function () {
      scroller.removeEventListener("scroll", scrollHandler);
      window.removeEventListener("resize", resizeHandler);
      table.style.removeProperty("--twc-compare-scroll-left");
      summaryViewport.style.removeProperty("margin-left");
      summaryViewport.style.removeProperty("width");
      summaryContainer.style.removeProperty("transform");
      summaryViewport.hidden = true;
    };
    updateFrozenColumnOffset();
    updateSummaryVisibility();
  }

  /**
   * @param {Array<{ selected: { id: string, url: string, currentPage?: boolean, type: string, title: string, image: string }, payload: { product_name: string, customer_id_product?: string, type: string, id: number | string, img?: string, price_min?: number, properties?: unknown[] } }>} products
   * @returns {string}
   */
  function renderMobile(products) {
    var rows = buildRows(products);
    var productHeader = products
      .map(function (entry) {
        var title = entry.payload.product_name || entry.selected.title || "Prodotto";
        var productCode =
          typeof entry.payload.customer_id_product === "string" && entry.payload.customer_id_product.trim()
            ? entry.payload.customer_id_product.trim()
            : String(entry.payload.id || "");

        return (
          '<li class="twc-compare-mobile-products__item' +
          (entry.selected.currentPage ? " twc-compare-mobile-products__item--current" : "") +
          '">' +
          renderCompareHeaderMedia(entry, false, "") +
          renderCurrentProductBadge(entry) +
          '<a href="' + escapeAttribute(entry.selected.url) + '">' + escapeHtml(title) + "</a>" +
          (productCode
            ? '<p class="twc-compare-mobile-products__code">Codice: ' + escapeHtml(productCode) + "</p>"
            : "") +
          ((entry.payload.img || entry.selected.image)
            ? '<img class="twc-compare-mobile-products__thumb" src="' +
              escapeAttribute(entry.payload.img || entry.selected.image) +
              '" alt="' +
              escapeAttribute(title) +
              '" loading="lazy">'
            : "") +
          renderCompareRemoveButton(entry) +
          "</li>"
        );
      })
      .join("");

    var rowCards = rows
      .map(function (row) {
        var values = row.values
          .map(function (value, index) {
            if (row.key === "__summary__") {
              return renderMobileSummaryCard(products[index], value);
            }

            var isBest = row.isComparable && row.bestValue !== null && value.comparableValue === row.bestValue;
            var productCode =
              (typeof products[index].payload.customer_id_product === "string" &&
                products[index].payload.customer_id_product.trim()) ||
              String(products[index].payload.id || "") ||
              "";
            var productName =
              productCode ||
              products[index].payload.product_name ||
              products[index].selected.title ||
              "Prodotto";
            var productImage = products[index].payload.img || products[index].selected.image || "";
            return (
              '<li class="twc-compare-mobile-row__value' +
              (isBest ? " twc-compare-mobile-row__value--best" : "") +
              '">' +
              '<div class="twc-compare-mobile-row__main">' +
              '<span class="twc-compare-mobile-row__product">' +
              escapeHtml(productCode ? "Codice: " + productCode : productName) +
              "</span>" +
              '<div class="twc-compare-mobile-row__text">' +
              renderValueWithRank(value) +
              "</div>" +
              "</div>" +
              (productImage
                ? '<img class="twc-compare-mobile-row__thumb" src="' +
                  escapeAttribute(productImage) +
                  '" alt="' +
                  escapeAttribute(productName) +
                  '" loading="lazy">'
                : "") +
              "</li>"
            );
          })
          .join("");

        return (
          '<article class="twc-compare-mobile-row">' +
          '<h3 class="twc-compare-mobile-row__title">' +
          escapeHtml(row.label) +
          "</h3>" +
          (row.subtitle ? '<p class="twc-compare-mobile-row__subtitle">' + escapeHtml(row.subtitle) + "</p>" : "") +
          '<ul class="twc-compare-mobile-row__values">' +
          values +
          "</ul>" +
          "</article>"
        );
      })
      .join("");

    return (
      '<section class="twc-compare-mobile">' +
      '<ul class="twc-compare-mobile-products">' +
      productHeader +
      "</ul>" +
      '<div class="twc-compare-mobile-rows">' +
      rowCards +
      "</div>" +
      "</section>"
    );
  }

  /**
   * @param {Array<{ id: string, url: string, currentPage?: boolean }>} selection
   * @param {{ persistSelection?: boolean }=} options
   * @returns {Promise<void>}
   */
  async function openCompareSelection(selection, options) {
    if (!modalElement || !tableHostElement || !bodyElement) {
      return;
    }

    var persistSelection = !options || options.persistSelection !== false;
    activeCompareSource = persistSelection ? "manual" : "transient";
    if (!persistSelection) {
      activeTransientSelection = selection;
    }

    if (selection.length < MIN_COMPARE_ITEMS) {
      showMessage(getMessages().needTwoProducts);
      return;
    }

    tableHostElement.innerHTML = '<p class="twc-compare-loading">' + escapeHtml(getMessages().loading) + "</p>";
    if (!modalElement.open) {
      window.dispatchEvent(new CustomEvent('custom:panel:open', { detail: 'compare' }));
      modalElement.showModal();
    }
    bodyElement.classList.add("twc-compare-open");

    var resolved = await Promise.all(
      selection.map(function (item) {
        return resolveProductPayloadResult(item);
      }),
    );
    var validProducts = resolved
      .map(function (result) {
        return result.entry;
      })
      .filter(function (entry) {
        return entry !== null;
      });
    var cleanedSelection = resolved
      .map(function (result, index) {
        if (result.shouldRemove) {
          return null;
        }
        if (result.entry) {
          return {
            id: result.entry.selected.id,
            url: result.entry.selected.url,
            currentPage: result.entry.selected.currentPage,
          };
        }
        return selection[index];
      })
      .filter(function (item) {
        return item !== null;
      });
    var selectionChanged = JSON.stringify(cleanedSelection) !== JSON.stringify(selection);
    if (selectionChanged && persistSelection) {
      saveSelection(cleanedSelection);
      syncControls(cleanedSelection);
      updateLauncher(cleanedSelection);
    } else if (!persistSelection) {
      activeTransientSelection = cleanedSelection;
    }

    if (validProducts.length < MIN_COMPARE_ITEMS) {
      tableHostElement.innerHTML = '<p class="twc-compare-empty">' + escapeHtml(getMessages().empty) + "</p>";
      return;
    }

    var isMobile = window.matchMedia("(max-width: 989px)").matches;
    tableHostElement.innerHTML = isMobile ? renderMobile(validProducts) : renderDesktop(validProducts);
    setupCompare360Media();
    attachModalActions();
    setupDesktopStickyHeaderBehavior(validProducts);
  }

  /**
   * @returns {void}
   */
  function closeCompare() {
    if (!modalElement || !bodyElement) {
      return;
    }

    if (modalElement.open) {
      modalElement.close();
    }
    bodyElement.classList.remove("twc-compare-open");
  }
  window.addEventListener('custom:panel:open', function (event) {
    if (event instanceof CustomEvent && event.detail === 'ring') closeCompare();
  });

  /**
   * @param {Array<{ id: string, url: string }>} selection
   * @returns {Promise<void>}
   */
  async function openCompare(selection) {
    await openCompareSelection(selection, { persistSelection: true });
  }

  /**
   * @param {HTMLButtonElement} button
   * @returns {Promise<void>}
   */
  async function handleCompareAddToCart(button) {
    var variantId = button.getAttribute("data-variant-id") || "";
    if (!variantId || button.disabled) {
      return;
    }

    var originalLabel = button.textContent || getMessages().addToCart;
    button.disabled = true;
    button.textContent = getMessages().addingToCart;

    try {
      var addResponse = await window.fetch(Theme.routes.cart_add_url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          items: [
            {
              id: Number(variantId),
              quantity: 1,
            },
          ],
        }),
      });
      var addPayload = await addResponse.json();

      if (!addResponse.ok || addPayload.status) {
        showMessage(addPayload.message || addPayload.description || getMessages().addToCartError);
        return;
      }

      var cartResource = null;
      try {
        var cartResponse = await window.fetch(Theme.routes.cart_url + ".js", {
          credentials: "same-origin",
        });
        if (cartResponse.ok) {
          cartResource = await cartResponse.json();
        }
      } catch (_cartError) {
        cartResource = null;
      }

      button.textContent = getMessages().addToCart;
      document.dispatchEvent(
        new CustomEvent("cart:update", {
          bubbles: true,
          detail: {
            resource: cartResource || addPayload,
            sourceId: "custom-product-compare",
            data: {
              source: "product-form-component",
              itemCount: 1,
              productId: button.getAttribute("data-product-id") || "",
              variantId: variantId,
            },
          },
        }),
      );
    } catch (_error) {
      showMessage(getMessages().addToCartError);
    } finally {
      if (button.isConnected) {
        button.textContent = originalLabel;
        button.disabled = false;
      }
    }
  }

  /**
   * @param {HTMLButtonElement} button
   * @returns {void}
   */
  function handleCompareBuyNow(button) {
    var variantId = button.getAttribute("data-variant-id") || "";
    if (!variantId || button.disabled) {
      return;
    }

    var buyNowUrl = buildBuyNowUrl(variantId);
    if (!buyNowUrl) {
      return;
    }

    window.location.href = buyNowUrl;
  }

  /**
   * @returns {void}
   */
  function attachModalActions() {
    if (!modalElement) {
      return;
    }

    modalElement.querySelectorAll("[data-compare-remove]").forEach(function (button) {
      if (!(button instanceof HTMLButtonElement)) {
        return;
      }

      button.onclick = function () {
        var removeId = button.dataset.compareRemove || "";
        if (activeCompareSource === "transient") {
          activeTransientSelection = activeTransientSelection.filter(function (item) {
            return item.id !== removeId;
          });
          if (activeTransientSelection.length < MIN_COMPARE_ITEMS) {
            closeCompare();
            return;
          }
          openCompareSelection(activeTransientSelection, { persistSelection: false });
          return;
        }

        var selection = loadSelection().filter(function (item) {
          return item.id !== removeId;
        });
        saveSelection(selection);
        syncControls(selection);
        updateLauncher(selection);
        if (selection.length < MIN_COMPARE_ITEMS) {
          closeCompare();
          return;
        }
        openCompare(selection);
      };
    });

    modalElement.querySelectorAll("[data-compare-360-open]").forEach(function (button) {
      if (!(button instanceof HTMLButtonElement)) {
        return;
      }

      button.onclick = function (event) {
        event.preventDefault();
        event.stopPropagation();
        showInlineCompare360Viewer(
          button.getAttribute("data-compare-360-url") || "",
          button.getAttribute("data-compare-360-title") || "Vista 360",
          button.getAttribute("data-compare-360-code") || "",
        );
      };
    });

    modalElement.querySelectorAll("[data-compare-variant-select]").forEach(function (select) {
      if (!(select instanceof HTMLSelectElement)) {
        return;
      }

      select.onchange = function () {
        var productId = select.getAttribute("data-product-id") || "";
        var addButton = modalElement.querySelector('[data-compare-add-to-cart][data-product-id="' + productId + '"]');
        var buyNowButton = modalElement.querySelector('[data-compare-buy-now][data-product-id="' + productId + '"]');
        if (!(addButton instanceof HTMLButtonElement)) {
          return;
        }

        var selectedOption = select.options[select.selectedIndex];
        var available = selectedOption ? selectedOption.getAttribute("data-available") === "true" : false;
        addButton.setAttribute("data-variant-id", select.value);
        addButton.disabled = !available;
        addButton.textContent = available ? getMessages().addToCart : getMessages().unavailable;
        if (buyNowButton instanceof HTMLButtonElement) {
          buyNowButton.setAttribute("data-variant-id", select.value);
          buyNowButton.disabled = !available;
          buyNowButton.textContent = available ? getMessages().buyNow : getMessages().unavailable;
        }
      };
    });

    modalElement.querySelectorAll("[data-compare-add-to-cart]").forEach(function (button) {
      if (!(button instanceof HTMLButtonElement)) {
        return;
      }

      button.onclick = function (event) {
        event.preventDefault();
        void handleCompareAddToCart(button);
      };
    });

    modalElement.querySelectorAll("[data-compare-buy-now]").forEach(function (button) {
      if (!(button instanceof HTMLButtonElement)) {
        return;
      }

      button.onclick = function (event) {
        event.preventDefault();
        handleCompareBuyNow(button);
      };
    });
  }

  /**
   * @param {Event} event
   * @returns {void}
   */
  function handleCompare360TriggerClick(event) {
    var target = event.target;
    if (!(target instanceof Element)) {
      return;
    }

    var button = target.closest("[data-compare-360-open]");
    if (!(button instanceof HTMLButtonElement)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    showInlineCompare360Viewer(
      button.getAttribute("data-compare-360-url") || "",
      button.getAttribute("data-compare-360-title") || "Vista 360",
      button.getAttribute("data-compare-360-code") || "",
    );
  }

  /**
   * @returns {void}
   */
  function bindControlEvents() {
    getControls().forEach(function (control) {
      if (control.input.dataset.compareBound === "true") {
        return;
      }

      control.input.dataset.compareBound = "true";
      control.input.addEventListener("change", async function () {
        var selection = loadSelection();
        var result = await updateSelection(selection, control.product, control.input.checked);

        if (result.message) {
          control.input.checked = false;
          showMessage(result.message);
          return;
        }

        saveSelection(result.nextSelection);
        syncControls(result.nextSelection);
        updateLauncher(result.nextSelection);
      });
    });
  }

  /**
   * @returns {void}
   */
  function bindRelatedCompareTriggers() {
    document.querySelectorAll("[data-custom-related-compare-trigger]").forEach(function (trigger) {
      if (!(trigger instanceof HTMLButtonElement) || trigger.dataset.relatedCompareBound === "true") {
        return;
      }

      trigger.dataset.relatedCompareBound = "true";
      trigger.addEventListener("click", function () {
        var sectionRoot = trigger.closest(".twc-metafield-recos__section") || trigger.closest(".section");
        if (!(sectionRoot instanceof Element)) {
          return;
        }

        var selection = readCompareListFromSection(sectionRoot);
        void openCompareSelection(selection, { persistSelection: false });
      });
    });
  }

  /**
   * @returns {void}
   */
  function refreshControlsFromStorage() {
    var selection = loadSelection();
    syncControls(selection);
    updateLauncher(selection);
    bindControlEvents();
  }

  /**
   * @returns {Promise<void>}
   */
  async function refreshStoredSelection() {
    var selection = loadSelection();
    if (!selection.length) {
      saveSelection([]);
      refreshControlsFromStorage();
      return;
    }

    var sanitized = await sanitizeSelectionFromSource(selection);
    saveSelection(sanitized);
    refreshControlsFromStorage();
  }

  /**
   * @returns {void}
   */
  function scheduleControlsRefresh() {
    if (controlsRefreshScheduled) {
      return;
    }

    controlsRefreshScheduled = true;
    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(function () {
        controlsRefreshScheduled = false;
        refreshControlsFromStorage();
        bindRelatedCompareTriggers();
      });
    });
  }

  /**
   * @returns {void}
   */
  function clearComparisonSelection() {
    if (activeCompareSource === "transient") {
      activeTransientSelection = [];
      closeCompare();
      return;
    }

    saveSelection([]);
    syncControls([]);
    updateLauncher([]);
    closeCompare();
  }

  /**
   * @returns {void}
   */
  function bindShellEvents() {
    if (!launcherShellElement || !launcherElement || !modalElement) {
      return;
    }

    launcherElement.addEventListener("click", function () {
      openCompare(loadSelection());
    });

    /** @type {Element[]} */
    var clearButtons = Array.prototype.slice.call(launcherShellElement.querySelectorAll("[data-compare-clear]"));
    Array.prototype.push.apply(
      clearButtons,
      Array.prototype.slice.call(modalElement.querySelectorAll("[data-compare-clear]")),
    );
    clearButtons.forEach(function (clearButton) {
      if (!(clearButton instanceof HTMLButtonElement)) {
        return;
      }

      clearButton.addEventListener("click", function () {
        clearComparisonSelection();
      });
    });

    modalElement.addEventListener("close", function () {
      if (typeof activeDesktopScrollCleanup === "function") {
        activeDesktopScrollCleanup();
        activeDesktopScrollCleanup = null;
      }
      if (bodyElement) {
        bodyElement.classList.remove("twc-compare-open");
      }
    });

    modalElement.addEventListener("click", function (event) {
      if (event.target === modalElement) {
        closeCompare();
      }
    });
    modalElement.addEventListener("click", handleCompare360TriggerClick);

    window.addEventListener("resize", function () {
      if (!modalElement.open) {
        return;
      }

      if (activeCompareSource === "transient") {
        openCompareSelection(activeTransientSelection, { persistSelection: false });
      } else {
        openCompare(loadSelection());
      }
      setupCompare360Media();
    });

    document.addEventListener("filter:update", function () {
      scheduleControlsRefresh();
    });

    var observer = new MutationObserver(function (mutations) {
      for (var index = 0; index < mutations.length; index += 1) {
        var mutation = mutations[index];
        for (var addedIndex = 0; addedIndex < mutation.addedNodes.length; addedIndex += 1) {
          var addedNode = mutation.addedNodes[addedIndex];
          if (!(addedNode instanceof Element)) {
            continue;
          }

          if (
            addedNode.matches(".twc-product-compare-toggle, .custom-confront-button") ||
            addedNode.matches("[data-custom-related-compare-trigger]") ||
            addedNode.querySelector(".twc-product-compare-toggle, .custom-confront-button, [data-custom-related-compare-trigger]")
          ) {
            scheduleControlsRefresh();
            return;
          }
        }
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  /**
   * @returns {void}
   */
  function init() {
    ensureShell();
    bindControlEvents();
    bindRelatedCompareTriggers();
    bindShellEvents();
    var selection = loadSelection();
    saveSelection(selection);
    refreshControlsFromStorage();
    refreshStoredSelection();
  }

  return {
    init: init,
  };
})();

if (window.CustomShopify.ProductComparison && typeof window.CustomShopify.ProductComparison.init === "function") {
  window.CustomShopify.ProductComparison.init();
}

window.CustomShopify.ReviewCarouselDots = (function () {
  /**
   * @param {HTMLElement} scroller
   * @param {HTMLElement[]} cards
   * @returns {number}
   */
  function getActiveIndex(scroller, cards) {
    var scrollerRect = scroller.getBoundingClientRect();
    var scrollerCenter = scrollerRect.left + scrollerRect.width / 2;
    var activeIndex = 0;
    var activeDistance = Number.POSITIVE_INFINITY;

    cards.forEach(function (card, index) {
      var cardRect = card.getBoundingClientRect();
      var cardCenter = cardRect.left + cardRect.width / 2;
      var distance = Math.abs(cardCenter - scrollerCenter);
      if (distance < activeDistance) {
        activeDistance = distance;
        activeIndex = index;
      }
    });

    return activeIndex;
  }

  /**
   * @param {Element[]} dots
   * @param {number} activeIndex
   * @returns {void}
   */
  function setActiveDot(dots, activeIndex) {
    dots.forEach(function (dot, index) {
      var isActive = index === activeIndex;
      dot.classList.toggle("twc-reviews__carousel-dot--active", isActive);
      if (isActive) {
        dot.setAttribute("aria-current", "true");
      } else {
        dot.removeAttribute("aria-current");
      }
    });
  }

  /**
   * @param {Element} root
   * @returns {void}
   */
  function initCarousel(root) {
    if (root.getAttribute("data-twc-review-carousel-bound") === "true") {
      return;
    }

    var scroller = root.querySelector(".twc-reviews__grid");
    if (!(scroller instanceof HTMLElement)) {
      return;
    }
    var carouselScroller = scroller;

    /** @type {HTMLElement[]} */
    var cards = [];
    /** @type {HTMLAnchorElement[]} */
    var dots = [];

    root.querySelectorAll("[data-twc-review-card]").forEach(function (card) {
      if (card instanceof HTMLElement && card.id) {
        cards.push(card);
      }
    });

    root.querySelectorAll("[data-twc-review-dot]").forEach(function (dot) {
      if (dot instanceof HTMLAnchorElement) {
        dots.push(dot);
      }
    });

    if (cards.length < 2 || dots.length < 2) {
      return;
    }

    root.setAttribute("data-twc-review-carousel-bound", "true");

    /**
     * @returns {void}
     */
    function updateActiveDotFromScroll() {
      setActiveDot(dots, getActiveIndex(carouselScroller, cards));
    }

    var scrollUpdateScheduled = false;
    carouselScroller.addEventListener(
      "scroll",
      function () {
        if (scrollUpdateScheduled) {
          return;
        }

        scrollUpdateScheduled = true;
        window.requestAnimationFrame(function () {
          scrollUpdateScheduled = false;
          updateActiveDotFromScroll();
        });
      },
      { passive: true },
    );

    dots.forEach(function (dot, index) {
      dot.addEventListener("click", function (event) {
        var card = cards[index];
        if (card) {
          event.preventDefault();
          card.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
            inline: "start",
          });
          dot.blur();
        }
        setActiveDot(dots, index);
      });
    });

    updateActiveDotFromScroll();
  }

  /**
   * @returns {void}
   */
  function init() {
    document.querySelectorAll(".twc-reviews--mobile-carousel").forEach(initCarousel);
  }

  return {
    init: init,
  };
})();

if (
  window.CustomShopify.ReviewCarouselDots &&
  typeof window.CustomShopify.ReviewCarouselDots.init === "function"
) {
  window.CustomShopify.ReviewCarouselDots.init();
}

window.CustomShopify.ChatLauncher = (function () {
  /**
   * @param {Record<string, unknown>} eventObject
   * @returns {Promise<unknown | null>}
   */
  function pushAnalyticsEvent(eventObject) {
    var analytics = window.CustomShopify && window.CustomShopify.Analytics;

    if (!analytics || typeof analytics.pushEvent !== "function") {
      return Promise.resolve(null);
    }

    return analytics.pushEvent(eventObject).catch(function () {
      return null;
    });
  }

  /**
   * @returns {"mobile" | "desktop"}
   */
  function getDeviceContext() {
    return isMobilePhoneEnvironment() ? "mobile" : "desktop";
  }

  /**
   * @param {HTMLElement} root
   * @param {boolean} open
   * @returns {void}
   */
  function setOpen(root, open) {
    var toggle = root.querySelector("[data-twc-chat-toggle]");

    root.dataset.open = open ? "true" : "false";
    if (toggle instanceof HTMLButtonElement) {
      toggle.setAttribute("aria-expanded", open ? "true" : "false");
    }
  }

  /**
   * @returns {boolean}
   */
  function isMobilePhoneEnvironment() {
    return window.matchMedia("(max-width: 749px)").matches || window.matchMedia("(pointer: coarse)").matches;
  }

  /**
   * @param {string} text
   * @returns {boolean}
   */
  function fallbackCopyText(text) {
    var textarea = document.createElement("textarea");

    textarea.value = text;
    textarea.setAttribute("readonly", "readonly");
    textarea.style.position = "fixed";
    textarea.style.top = "-1000px";
    textarea.style.left = "-1000px";
    document.body.appendChild(textarea);
    textarea.select();

    try {
      return document.execCommand("copy");
    } catch (_error) {
      return false;
    } finally {
      textarea.remove();
    }
  }

  /**
   * @param {string} text
   * @returns {Promise<boolean>}
   */
  async function copyText(text) {
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch (_error) {
        return fallbackCopyText(text);
      }
    }

    return fallbackCopyText(text);
  }

  /**
   * @param {HTMLElement} root
   * @returns {HTMLDialogElement | null}
   */
  function getDialog(root) {
    var dialog = root.querySelector("[data-twc-chat-copy-dialog]");

    return dialog instanceof HTMLDialogElement ? dialog : null;
  }

  /**
   * @param {HTMLElement} root
   * @param {boolean} copied
   * @returns {void}
   */
  function showPhoneDialog(root, copied) {
    var dialog = getDialog(root);

    if (!dialog) {
      return;
    }

    var message = dialog.querySelector("[data-twc-chat-copy-message]");
    if (message instanceof HTMLElement) {
      message.textContent = copied ? dialog.dataset.copiedMessage || "" : dialog.dataset.fallbackMessage || "";
    }

    if (!dialog.open) {
      dialog.showModal();
    }
  }

  /**
   * @param {HTMLElement} root
   * @returns {void}
   */
  function bindPhoneAction(root) {
    var phoneAction = root.querySelector("[data-twc-chat-phone]");

    if (!(phoneAction instanceof HTMLAnchorElement)) {
      return;
    }

    var phoneLink = phoneAction;

    phoneAction.addEventListener("click", async function (event) {
      if (isMobilePhoneEnvironment()) {
        pushAnalyticsEvent({
          event: "chat:phone_clicked",
          channel: "phone",
          action: "dial",
          device_context: getDeviceContext(),
        });
        return;
      }

      var phoneNumber = phoneLink.dataset.phoneNumber || "";
      if (!phoneNumber) {
        return;
      }

      event.preventDefault();
      pushAnalyticsEvent({
        event: "chat:phone_clicked",
        channel: "phone",
        action: "copy",
        device_context: getDeviceContext(),
      });
      setOpen(root, false);
      var copied = await copyText(phoneNumber);
      showPhoneDialog(root, copied);
      pushAnalyticsEvent({
        event: "chat:phone_copy_result",
        channel: "phone",
        action: copied ? "copied" : "fallback_shown",
        device_context: getDeviceContext(),
      });
    });
  }

  /**
   * @param {HTMLElement} root
   * @returns {void}
   */
  function bindChannelActions(root) {
    var whatsappAction = root.querySelector(".twc-chat-launcher__action--whatsapp");
    var emailAction = root.querySelector(".twc-chat-launcher__action--email");

    if (whatsappAction instanceof HTMLAnchorElement) {
      whatsappAction.addEventListener("click", function () {
        pushAnalyticsEvent({
          event: "chat:whatsapp_clicked",
          channel: "whatsapp",
          action: "open",
          device_context: getDeviceContext(),
        });
        setOpen(root, false);
      });
    }

    if (emailAction instanceof HTMLAnchorElement) {
      var emailLink = emailAction;

      emailLink.addEventListener("click", function (event) {
        if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) {
          pushAnalyticsEvent({
            event: "chat:email_clicked",
            channel: "email",
            action: "open",
            device_context: getDeviceContext(),
          });
          return;
        }

        event.preventDefault();
        setOpen(root, false);
        pushAnalyticsEvent({
          event: "chat:email_clicked",
          channel: "email",
          action: "open",
          device_context: getDeviceContext(),
        }).finally(function () {
          window.location.href = emailLink.href;
        });
      });
    }
  }

  /**
   * @param {HTMLElement} root
   * @returns {void}
   */
  function bindDialog(root) {
    var dialog = getDialog(root);

    if (!dialog) {
      return;
    }

    var activeDialog = dialog;

    activeDialog.addEventListener("click", function (event) {
      if (event.target === activeDialog) {
        activeDialog.close();
      }
    });
  }

  /**
   * @param {Element} element
   * @returns {void}
   */
  function initRoot(element) {
    if (!(element instanceof HTMLElement) || element.dataset.twcChatBound === "true") {
      return;
    }

    var toggle = element.querySelector("[data-twc-chat-toggle]");
    if (!(toggle instanceof HTMLButtonElement)) {
      return;
    }

    element.dataset.twcChatBound = "true";
    setOpen(element, false);

    toggle.addEventListener("click", function () {
      var nextOpenState = element.dataset.open !== "true";
      setOpen(element, nextOpenState);
      pushAnalyticsEvent({
        event: "chat:launcher_toggled",
        channel: "launcher",
        action: nextOpenState ? "open" : "close",
        device_context: getDeviceContext(),
      });
    });

    document.addEventListener("click", function (event) {
      var target = event.target;
      if (target instanceof Node && !element.contains(target)) {
        setOpen(element, false);
      }
    });

    document.addEventListener("keydown", function (event) {
      if (event.key === "Escape") {
        setOpen(element, false);
      }
    });

    bindChannelActions(element);
    bindPhoneAction(element);
    bindDialog(element);
  }

  /**
   * @returns {void}
   */
  function init() {
    document.querySelectorAll("[data-twc-chat-launcher]").forEach(initRoot);
  }

  return {
    init: init,
  };
})();

if (window.CustomShopify.ChatLauncher && typeof window.CustomShopify.ChatLauncher.init === "function") {
  window.CustomShopify.ChatLauncher.init();
}

window.CustomShopify.BirthdayCardSelector = (function () {
  /**
   * @param {HTMLElement} root
   * @returns {HTMLDialogElement | null}
   */
  function getDialog(root) {
    var dialog = root.querySelector("[data-twc-birthday-card-dialog]");

    return dialog instanceof HTMLDialogElement ? dialog : null;
  }

  /**
   * @param {HTMLElement} root
   * @returns {HTMLInputElement | null}
   */
  function getInput(root) {
    var input = root.querySelector("[data-twc-birthday-card-input]");

    return input instanceof HTMLInputElement ? input : null;
  }

  /**
   * @param {HTMLElement} root
   * @returns {HTMLElement | null}
   */
  function getSummary(root) {
    var summary = root.querySelector("[data-twc-birthday-card-summary]");

    return summary instanceof HTMLElement ? summary : null;
  }

  /**
   * @param {HTMLElement} root
   * @returns {HTMLElement | null}
   */
  function getSummaryValue(root) {
    var summaryValue = root.querySelector("[data-twc-birthday-card-summary-value]");

    return summaryValue instanceof HTMLElement ? summaryValue : null;
  }

  /**
   * @param {HTMLElement} root
   * @returns {HTMLElement | null}
   */
  function getPreviewArea(root) {
    var previewArea = root.querySelector("[data-twc-birthday-card-preview-area]");

    return previewArea instanceof HTMLElement ? previewArea : null;
  }

  /**
   * @param {boolean} isLocked
   * @returns {void}
   */
  function setPageScrollLocked(isLocked) {
    document.documentElement.classList.toggle("twc-birthday-card-selector-scroll-lock", isLocked);

    if (document.body) {
      document.body.classList.toggle("twc-birthday-card-selector-scroll-lock", isLocked);
    }
  }

  /**
   * @param {HTMLElement} preview
   * @param {number} index
   * @returns {void}
   */
  function updateCarouselDots(preview, index) {
    preview.querySelectorAll("[data-twc-birthday-card-carousel-dot]").forEach(function (dot) {
      if (!(dot instanceof HTMLElement)) {
        return;
      }

      dot.setAttribute("aria-pressed", dot.dataset.carouselIndex === String(index) ? "true" : "false");
    });
  }

  /**
   * @param {HTMLElement} preview
   * @returns {void}
   */
  function resetCarousel(preview) {
    var carousel = preview.querySelector("[data-twc-birthday-card-carousel]");

    if (!(carousel instanceof HTMLElement)) {
      return;
    }

    carousel.scrollLeft = 0;
    updateCarouselDots(preview, 0);
  }

  /**
   * @param {HTMLElement} carousel
   * @returns {number}
   */
  function getCarouselIndex(carousel) {
    var slideWidth = carousel.clientWidth;

    if (!slideWidth) {
      return 0;
    }

    return Math.round(carousel.scrollLeft / slideWidth);
  }

  /**
   * @param {HTMLElement} root
   * @param {string} value
   * @returns {void}
   */
  function updatePreview(root, value) {
    var previewArea = getPreviewArea(root);
    var hasActivePreview = false;

    root.querySelectorAll("[data-twc-birthday-card-preview]").forEach(function (preview) {
      if (!(preview instanceof HTMLElement)) {
        return;
      }

      var isActivePreview = Boolean(value) && preview.dataset.cardValue === value;
      preview.hidden = !isActivePreview;
      hasActivePreview = hasActivePreview || isActivePreview;

      if (isActivePreview) {
        resetCarousel(preview);
      }
    });

    if (previewArea) {
      previewArea.hidden = !hasActivePreview;
    }
  }

  /**
   * @param {HTMLElement} root
   * @param {string} value
   * @returns {void}
   */
  function setChoiceState(root, value) {
    root.querySelectorAll("[data-twc-birthday-card-choice]").forEach(function (choice) {
      if (!(choice instanceof HTMLElement)) {
        return;
      }

      choice.setAttribute("aria-pressed", choice.dataset.cardValue === value ? "true" : "false");
    });

    updatePreview(root, value);
    root.dataset.twcBirthdayCardPendingValue = value;
  }

  /**
   * @param {HTMLElement} root
   * @returns {string}
   */
  function getSelectedValue(root) {
    var input = getInput(root);

    return input ? input.value : "";
  }

  /**
   * @param {HTMLElement} root
   * @param {string} value
   * @returns {void}
   */
  function setSelectedValue(root, value) {
    var input = getInput(root);
    var summary = getSummary(root);
    var summaryValue = getSummaryValue(root);

    if (input) {
      input.value = value;
    }

    setChoiceState(root, value);

    if (!summary || !summaryValue) {
      return;
    }

    summaryValue.textContent = value;
    summary.hidden = !value;
  }

  /**
   * @param {HTMLElement} root
   * @returns {void}
   */
  function initRoot(root) {
    if (root.dataset.twcBirthdayCardBound === "true") {
      return;
    }

    var dialog = getDialog(root);
    var openButton = root.querySelector("[data-twc-birthday-card-open]");

    if (!dialog || !(openButton instanceof HTMLButtonElement)) {
      return;
    }

    var activeDialog = dialog;
    root.dataset.twcBirthdayCardBound = "true";

    openButton.addEventListener("click", function () {
      if (!activeDialog.open) {
        setChoiceState(root, getSelectedValue(root));
        activeDialog.showModal();
        setPageScrollLocked(true);
      }
    });

    root.querySelectorAll("[data-twc-birthday-card-choice]").forEach(function (choice) {
      if (!(choice instanceof HTMLElement)) {
        return;
      }

      choice.addEventListener("click", function () {
        setChoiceState(root, choice.dataset.cardValue || "");
      });
    });

    root.querySelectorAll("[data-twc-birthday-card-clear]").forEach(function (button) {
      if (!(button instanceof HTMLButtonElement)) {
        return;
      }

      button.addEventListener("click", function () {
        setSelectedValue(root, "");
      });
    });

    root.querySelectorAll("[data-twc-birthday-card-confirm]").forEach(function (button) {
      if (!(button instanceof HTMLButtonElement)) {
        return;
      }

      button.addEventListener("click", function () {
        setSelectedValue(root, root.dataset.twcBirthdayCardPendingValue || "");
        activeDialog.close();
      });
    });

    root.querySelectorAll("[data-twc-birthday-card-carousel-dot]").forEach(function (dot) {
      if (!(dot instanceof HTMLButtonElement)) {
        return;
      }

      dot.addEventListener("click", function () {
        var preview = dot.closest("[data-twc-birthday-card-preview]");
        var carousel = preview ? preview.querySelector("[data-twc-birthday-card-carousel]") : null;
        var index = Number(dot.dataset.carouselIndex || "0");

        if (!(preview instanceof HTMLElement) || !(carousel instanceof HTMLElement)) {
          return;
        }

        carousel.scrollTo({
          left: carousel.clientWidth * index,
          behavior: "smooth",
        });
        updateCarouselDots(preview, index);
      });
    });

    root.querySelectorAll("[data-twc-birthday-card-carousel]").forEach(function (carousel) {
      if (!(carousel instanceof HTMLElement)) {
        return;
      }

      carousel.addEventListener("scroll", function () {
        var preview = carousel.closest("[data-twc-birthday-card-preview]");

        if (preview instanceof HTMLElement) {
          updateCarouselDots(preview, getCarouselIndex(carousel));
        }
      });
    });

    root.querySelectorAll("[data-twc-birthday-card-close]").forEach(function (button) {
      if (!(button instanceof HTMLButtonElement)) {
        return;
      }

      button.addEventListener("click", function () {
        activeDialog.close();
      });
    });

    activeDialog.addEventListener("click", function (event) {
      if (event.target === activeDialog) {
        activeDialog.close();
      }
    });

    activeDialog.addEventListener("close", function () {
      setChoiceState(root, getSelectedValue(root));
      setPageScrollLocked(false);
    });
  }

  /**
   * @returns {void}
   */
  function init() {
    document.querySelectorAll("[data-twc-birthday-card-selector]").forEach(function (root) {
      if (root instanceof HTMLElement) {
        initRoot(root);
      }
    });
  }

  return {
    init: init,
  };
})();

if (
  window.CustomShopify.BirthdayCardSelector &&
  typeof window.CustomShopify.BirthdayCardSelector.init === "function"
) {
  window.CustomShopify.BirthdayCardSelector.init();
}

window.CustomShopify.deferredReady = true;



